import type { BackupVault, InventoryResource } from '@repo/api-gw-sdk';
import {
  BackupStatus,
  Provider,
  DataClass,
  Environment,
  ResourceType,
  SecurityScanConclusionEnum,
  KubernetesAccessMode,
  SecurityAssignmentStatus,
} from '@repo/api-gw-sdk';

import type { DemoSettings } from '@/contexts/environment';
import { Regions } from '@/data/inventory/regions';
import { ResourceTypes } from '@/data/inventory/resourceTypes';
import dayjs from '@/utils/dayjs';
import { convertToBytes } from '@/utils/fileSizeFormatter';

import { backupPoliciesDAL } from './backupPoliciesDAL';

import { sourceAccountsDAL } from '../source-accounts/sourceAccountsDAL';
import { vaultsDAL } from '../vaults/vaultsDAL';

/**
 * Generates a random alphanumeric string of the specified length
 * @param length The length of the string to generate
 * @returns A random alphanumeric string
 */
const generateRandomAlphanumeric = (length: number): string => {
  const chars = 'abcdefghijklmnopqrstuvwxyz0123456789';
  let result = '';
  for (let i = 0; i < length; i++) {
    result += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return result;
};

/**
 * Updates the providerResourceId of an InventoryResource based on its resourceType
 * @param resource The inventory resource to update
 * @returns The updated resource with a new providerResourceId
 */
const generateProviderResourceId = (resource: InventoryResource): string => {
  if (resource.providerResourceId) {
    return resource.providerResourceId;
  }

  switch (resource.resourceType) {
    // AWS Resources
    case ResourceType.AwsEc2:
      return `i-${generateRandomAlphanumeric(8)}`;
    case ResourceType.AwsRds:
      return `db-${generateRandomAlphanumeric(10)}`;
    case ResourceType.AwsS3:
      return resource.resourceName.toLowerCase().replace(/[^a-z0-9-]/g, '-');
    case ResourceType.AwsEfs:
      return `fs-${generateRandomAlphanumeric(8)}`;

    case ResourceType.AwsEksNamespace:
      return `eks-${resource.resourceName.toLowerCase()}`;

    case ResourceType.AwsDynamoDb:
      return resource.resourceName.replace(/\s+/g, '-');

    // Azure Resources
    case ResourceType.AzureVirtualMachine:
      return `/subscriptions/${resource.providerAccountId}/resourceGroups/rg-${resource.resourceName}/providers/Microsoft.Compute/virtualMachines/${resource.resourceName}`;

    case ResourceType.AzureFileShare:
      return `/subscriptions/${resource.providerAccountId}/resourceGroups/rg-storage/providers/Microsoft.Storage/fileShares/${resource.resourceName}`;

    case ResourceType.AzureStorageAccount:
      return `/subscriptions/${resource.providerAccountId}/resourceGroups/rg-storage/providers/Microsoft.Storage/storageAccounts/${resource.resourceName.toLowerCase().replace(/[^a-z0-9]/g, '')}`;

    case ResourceType.AzureDisk:
      return `/subscriptions/${resource.providerAccountId}/resourceGroups/rg-disks/providers/Microsoft.Compute/disks/${resource.resourceName}`;

    case ResourceType.AzureSqlDatabase:
      return `/subscriptions/${resource.providerAccountId}/resourceGroups/rg-sql/providers/Microsoft.Sql/servers/sql-server-${resource.providerAccountId.substring(0, 6)}/databases/${resource.resourceName}`;

    case ResourceType.AzureSqlManagedInstance:
      return `/subscriptions/${resource.providerAccountId}/resourceGroups/rg-sql/providers/Microsoft.Sql/managedInstances/${resource.resourceName}`;

    case ResourceType.AzureMysql:
      return `/subscriptions/${resource.providerAccountId}/resourceGroups/rg-mysql/providers/Microsoft.DBforMySQL/flexibleServers/${resource.resourceName.toLowerCase().replace(/[^a-z0-9]/g, '-')}`;

    case ResourceType.AzurePostgresql:
      return `/subscriptions/${resource.providerAccountId}/resourceGroups/rg-postgres/providers/Microsoft.DBforPostgreSQL/flexibleServers/${resource.resourceName.toLowerCase().replace(/[^a-z0-9]/g, '-')}`;

    case ResourceType.AzureCosmosdbMongodb:
      return `/subscriptions/${resource.providerAccountId}/resourceGroups/rg-cosmos/providers/Microsoft.DocumentDB/databaseAccounts/${resource.resourceName.toLowerCase().replace(/[^a-z0-9]/g, '')}-mongodb`;

    case ResourceType.AzureCosmosdbNosql:
      return `/subscriptions/${resource.providerAccountId}/resourceGroups/rg-cosmos/providers/Microsoft.DocumentDB/databaseAccounts/${resource.resourceName.toLowerCase().replace(/[^a-z0-9]/g, '')}-nosql`;

    case ResourceType.AzureSqlVirtualMachine:
      return `/subscriptions/${resource.providerAccountId}/resourceGroups/rg-sqlvm/providers/Microsoft.SqlVirtualMachine/SqlVirtualMachines/${resource.resourceName.toLowerCase().replace(/[^a-z0-9]/g, '-')}`;

    // GCP Resources
    case ResourceType.GcpComputeEngineInstance:
      return `projects/${resource.providerAccountId}/zones/${resource.region}/instances/${resource.resourceName.toLowerCase().replace(/\s+/g, '-')}`;

    case ResourceType.GcpCloudSqlInstance:
      return `projects/${resource.providerAccountId}/instances/${resource.resourceName.toLowerCase().replace(/\s+/g, '-')}`;

    case ResourceType.GcpCloudStorageBucket:
      return resource.resourceName.toLowerCase().replace(/[^a-z0-9-]/g, '-');

    case ResourceType.GcpGkeNamespace:
      return `projects/${resource.providerAccountId}/zones/${resource.region}/clusters/gke-cluster/namespaces/${resource.resourceName.toLowerCase().replace(/\s+/g, '-')}`;

    case ResourceType.GcpBigQuery:
      return `projects/${resource.providerAccountId}/datasets/${resource.resourceName.toLowerCase().replace(/\s+/g, '_')}`;

    case ResourceType.GcpCloudFirestore:
      return `projects/${resource.providerAccountId}/databases/${resource.resourceName.toLowerCase().replace(/\s+/g, '-')}`;

    // Atlas MongoDB
    case ResourceType.AtlasMongodbCluster:
      return `${resource.resourceName.toLowerCase().replace(/\s+/g, '-')}-cluster`;

    // Default case
    case ResourceType.ResourceTypeUnspecified:
    default:
      return generateRandomAlphanumeric(8);
  }
};

const getBackupPolicies = (
  resource: InventoryResource,
  demoSettings: DemoSettings
) => {
  const {
    internalSystemsPolicy,
    productionWithPIIPolicy,
    productionWithoutPIIPolicy,
    productionDatabasesPolicy,
  } = backupPoliciesDAL.getMap(demoSettings);
  const policies = [];

  if (
    resource.classifications?.environmentDetails?.environment ===
    Environment.Prod
  ) {
    if (
      resource.resourceType === ResourceType.AwsRds ||
      resource.resourceType === ResourceType.AzureSqlManagedInstance ||
      resource.resourceType === ResourceType.GcpCloudSqlInstance ||
      ((resource.resourceType === ResourceType.AwsEc2 ||
        resource.resourceType === ResourceType.GcpComputeEngineInstance ||
        resource.resourceType === ResourceType.AzureVirtualMachine) &&
        resource.classifications?.appsDetails?.apps?.some(
          (app) =>
            app.name === 'MySQL' ||
            app.name === 'PostgreSQL' ||
            app.name === 'MongoDB'
        ))
    ) {
      policies.push(productionDatabasesPolicy);
    }
    if (
      resource.classifications?.dataClassesDetails?.dataClasses?.includes(
        DataClass.Pii
      ) ||
      resource.classifications?.dataClassesDetails?.dataClasses?.includes(
        DataClass.Fi
      )
    ) {
      policies.push(productionWithPIIPolicy);
    } else {
      policies.push(productionWithoutPIIPolicy);
    }
  }

  if (
    resource.classifications?.environmentDetails?.environment ===
    Environment.ProdInternal
  ) {
    policies.push(internalSystemsPolicy);
  }

  return policies.length ? [policies[0]] : [];
};

const vpcs: Record<string, string> = {
  [Provider.Aws]: 'vpc-0c09a212aab3f749d',
  [Provider.Azure]: 'virtual-network-1',
};

const VirtualMachinesResourceTypes = {
  [Provider.Aws]: ResourceType.AwsEc2,
  [Provider.Azure]: ResourceType.AzureVirtualMachine,
  [Provider.Gcp]: ResourceType.GcpComputeEngineInstance,
};

const VirtualMachinesDbResourceTypes = {
  [Provider.Aws]: ResourceType.ResourceTypeUnspecified,
  [Provider.Azure]: ResourceType.AzureSqlVirtualMachine,
  [Provider.Gcp]: ResourceType.ResourceTypeUnspecified,
};

export const ManagedSQLResourceTypes = {
  [Provider.Aws]: ResourceType.AwsRds,
  [Provider.Azure]: ResourceType.AzureSqlManagedInstance,
  [Provider.Gcp]: ResourceType.GcpCloudSqlInstance,
};

const StorageResourceTypes = {
  [Provider.Aws]: ResourceType.AwsS3,
  [Provider.Azure]: ResourceType.AzureStorageAccount,
  [Provider.Gcp]: ResourceType.GcpCloudStorageBucket,
};

export const NoSqlDBResourceTypes = {
  [Provider.Aws]: ResourceType.AwsDynamoDb,
  [Provider.Azure]: ResourceType.AzureCosmosdbNosql,
  [Provider.Gcp]: ResourceType.GcpCloudFirestore,
};

const KubernetesResourceTypes = {
  [Provider.Aws]: ResourceType.AwsEksNamespace,
  [Provider.Azure]: ResourceType.AzureAksNamespace,
  [Provider.Gcp]: ResourceType.GcpGkeNamespace,
};

export const inventoryData = (demoSettings: DemoSettings) => {
  const now = new Date();
  const vaults = vaultsDAL.list(demoSettings);
  const sourceAccounts = sourceAccountsDAL.list(demoSettings);
  backupPoliciesDAL.list(demoSettings).forEach((policy) => {
    policy.attachedResources = 0;
  });

  // Create inventory items with providerResourceId initially set to empty string
  // The updateProviderResourceId function will set the appropriate value based on resourceType

  const secondaryCloudProviders: (keyof typeof VirtualMachinesResourceTypes)[] =
    Array.from(
      new Set([
        demoSettings.primaryProvider,
        ...(demoSettings.AWS ? [Provider.Aws as const] : []),
        ...(demoSettings.AZURE ? [Provider.Azure as const] : []),
        ...(demoSettings.GCP ? [Provider.Gcp as const] : []),
      ])
    );

  const inventory: InventoryResource[] = [
    {
      resourceName: 'Bookings-prod-server-1',
      resourceType: VirtualMachinesResourceTypes[demoSettings.primaryProvider],
      backupStatus: BackupStatus.Protected,
      classifications: {
        dataClassesDetails: {
          dataClasses: [DataClass.Fi, DataClass.Pii],
        },
        environmentDetails: {
          environment: Environment.Prod,
        },
        appsDetails: {
          apps: [
            {
              name: 'Apache Tomcat',
            },
          ],
        },
      },
      subnets: ['10.0.0.0/8'],
      tags: { Env: 'Prod', Web: '' },
      sourceStorage: {
        filesCount: 20_000,
        sizeBytes: convertToBytes(80, 'GiB'),
        volumeCount: 1,
      },
      latestSnapshotTime: new Date(new Date(now).setHours(now.getHours() - 12)),
      discoveredTime: new Date(new Date(now).setHours(now.getHours() - 12)),
      snapshotStorage: {
        sizeBytes: convertToBytes(120, 'GB'),
        eonSnapshotCount: 0,
        nonEonSnapshotCount: 19,
      },
      id: '',
      region: '',
      backupPolicies: [],
      securityAssignmentStatus: SecurityAssignmentStatus.SECURITY_ASSIGNED,
      vaults: [],
      providerAccountId: '',
      cloudProvider: Provider.ProviderUnspecified,
      providerResourceId: '',
    },
    {
      resourceName: 'db-workflows-2',
      resourceType: ManagedSQLResourceTypes[demoSettings.primaryProvider],
      backupStatus: BackupStatus.Protected,
      classifications: {
        environmentDetails: {
          environment: Environment.Prod,
        },
        appsDetails: {
          apps: [
            {
              name: 'MySQL',
            },
          ],
        },
        dataClassesDetails: {
          dataClasses: [DataClass.Fi, DataClass.Pii],
        },
      },
      tags: { Env: 'Prod' },
      subnets: ['10.0.0.0/8'],
      latestSnapshotTime: new Date(new Date(now).setHours(now.getHours() - 11)),
      discoveredTime: new Date(new Date(now).setHours(now.getHours() - 11)),
      resourceProperties: {
        awsRds: {
          sizeBytes: 0,
          clusterId: 'cluster-1',
          engine: 'postgres',
          engineVersion: '13.3',
          storageSettings: {
            type: 'gp2',
            iops: 100,
            throughput: 343,
          },
          databaseName: 'workflows_db',
          instanceClass: 'db.t3.micro',
          isMultiAZ: false,
          isPubliclyAccessible: true,
        },
        gcpCloudSql: {
          engine: 'postgres',
          engineVersion: '13.3',
          instanceTier: '',
          sizeBytes: 0,
        },
      },
      sourceStorage: {
        filesCount: 0,
        sizeBytes: convertToBytes(150, 'GiB'),
        volumeCount: 2,
      },
      snapshotStorage: {
        sizeBytes: convertToBytes(100, 'GB'),
        eonSnapshotCount: 0,
        nonEonSnapshotCount: 13,
      },
      id: '',
      region: '',
      backupPolicies: [],
      securityAssignmentStatus: SecurityAssignmentStatus.SECURITY_ASSIGNED,
      vaults: [],
      providerAccountId: '',
      cloudProvider: Provider.ProviderUnspecified,
      providerResourceId: '',
    },
    {
      resourceName: 'Bookings-prod-web-1',
      resourceType:
        VirtualMachinesResourceTypes[
          secondaryCloudProviders[2 % secondaryCloudProviders.length]
        ],
      classifications: {
        environmentDetails: {
          environment: Environment.Prod,
        },
        appsDetails: {
          apps: [
            {
              name: 'Nginx',
            },
          ],
        },
      },
      backupStatus: BackupStatus.Protected,
      tags: { Env: 'Prod', Web: '' },
      subnets: ['10.0.0.0/8'],
      latestSnapshotTime: new Date(new Date(now).setHours(now.getHours() - 20)),
      discoveredTime: new Date(new Date(now).setHours(now.getHours() - 20)),
      sourceStorage: {
        filesCount: 127_000,
        sizeBytes: convertToBytes(250, 'GiB'),
        volumeCount: 2,
      },
      snapshotStorage: {
        sizeBytes: convertToBytes(350, 'GB'),
        eonSnapshotCount: 0,
        nonEonSnapshotCount: 17,
      },
      id: '',
      region: '',
      backupPolicies: [],
      securityAssignmentStatus: SecurityAssignmentStatus.SECURITY_ASSIGNED,
      vaults: [],
      providerAccountId: '',
      cloudProvider: Provider.ProviderUnspecified,
      providerResourceId: '',
    },
    {
      resourceName: 'employee-list',
      resourceType: StorageResourceTypes[demoSettings.primaryProvider],
      classifications: {
        environmentDetails: {
          environment: Environment.ProdInternal,
        },
        dataClassesDetails: {
          dataClasses: [DataClass.Pii],
        },
      },
      backupStatus: BackupStatus.ViolationsDetected,
      tags: { Env: 'Internal production' },
      subnets: ['10.0.0.0/8'],
      latestSnapshotTime: new Date(new Date(now).setHours(now.getHours() - 2)),
      discoveredTime: new Date(new Date(now).setHours(now.getHours() - 2)),
      sourceStorage: {
        filesCount: 720,
        sizeBytes: convertToBytes(42, 'GiB'),
        volumeCount: 0,
      },
      snapshotStorage: {
        sizeBytes: convertToBytes(54, 'GB'),
        eonSnapshotCount: 0,
        nonEonSnapshotCount: 0,
      },
      controlViolationCounts: {
        medium: 1,
        low: 1,
        muted: 1,
      },
      id: '',
      region: '',
      backupPolicies: [],
      securityAssignmentStatus: SecurityAssignmentStatus.SECURITY_ASSIGNED,
      vaults: [],
      providerAccountId: '',
      cloudProvider: Provider.ProviderUnspecified,
      providerResourceId: '',
    },
    {
      resourceName: 'app-server-1',
      resourceType:
        VirtualMachinesResourceTypes[
          secondaryCloudProviders[1 % secondaryCloudProviders.length]
        ],
      classifications: {
        environmentDetails: {
          environment: Environment.Prod,
        },
        appsDetails: {
          apps: [
            {
              name: 'Redis',
            },
          ],
        },
        dataClassesDetails: {
          dataClasses: [DataClass.Pii],
        },
      },
      backupStatus: BackupStatus.Protected,
      tags: {},
      subnets: ['10.0.0.0/8'],
      latestSnapshotTime: new Date(new Date(now).setHours(now.getHours() - 4)),
      discoveredTime: new Date(new Date(now).setHours(now.getHours() - 4)),
      sourceStorage: {
        filesCount: 423_000,
        sizeBytes: convertToBytes(250, 'GiB'),
        volumeCount: 2,
      },
      snapshotStorage: {
        sizeBytes: convertToBytes(531, 'GB'),
        eonSnapshotCount: 0,
        nonEonSnapshotCount: 3,
      },
      id: '',
      region: '',
      backupPolicies: [],
      securityAssignmentStatus: SecurityAssignmentStatus.SECURITY_ASSIGNED,
      vaults: [],
      providerAccountId: '',
      cloudProvider: Provider.ProviderUnspecified,
      providerResourceId: '',
    },
    {
      resourceName: 'db-vm',
      resourceType: VirtualMachinesDbResourceTypes[Provider.Azure],
      classifications: {
        environmentDetails: {
          environment: Environment.Prod,
        },
        appsDetails: {
          apps: [
            {
              name: 'MSSQLServer',
            },
          ],
        },
      },
      backupStatus: BackupStatus.Protected,
      tags: {},
      subnets: ['10.0.0.0/8'],
      latestSnapshotTime: new Date(new Date(now).setHours(now.getHours() - 4)),
      discoveredTime: new Date(new Date(now).setHours(now.getHours() - 4)),
      sourceStorage: {
        filesCount: 423_000,
        sizeBytes: convertToBytes(250, 'GiB'),
        volumeCount: 2,
      },
      snapshotStorage: {
        sizeBytes: convertToBytes(531, 'GB'),
        eonSnapshotCount: 0,
        nonEonSnapshotCount: 3,
      },
      id: '',
      region: '',
      backupPolicies: [],
      securityAssignmentStatus: SecurityAssignmentStatus.SECURITY_ASSIGNED,
      vaults: [],
      providerAccountId: '',
      cloudProvider: Provider.ProviderUnspecified,
      providerResourceId: '',
    },
    {
      backupStatus: BackupStatus.Protected,
      resourceName: 'sqlim1',
      resourceType:
        ManagedSQLResourceTypes[
          secondaryCloudProviders[1 % secondaryCloudProviders.length]
        ],
      classifications: {
        environmentDetails: {
          environment: Environment.Prod,
        },
        appsDetails: {
          apps: [
            {
              name: 'MSSQLServer',
            },
          ],
        },
      },
      latestSnapshotTime: new Date(new Date(now).setHours(now.getHours() - 4)),
      discoveredTime: new Date(new Date(now).setHours(now.getHours() - 4)),
      tags: {},
      snapshotStorage: {},
      sourceStorage: {
        sizeBytes: 274877906944,
      },
      id: '',
      region: '',
      backupPolicies: [],
      securityAssignmentStatus: SecurityAssignmentStatus.SECURITY_ASSIGNED,
      vaults: [],
      providerAccountId: '',
      cloudProvider: Provider.ProviderUnspecified,
      providerResourceId: '',
    },
    {
      resourceName: 'ads-info',
      resourceType: NoSqlDBResourceTypes[demoSettings.primaryProvider],
      classifications: {
        environmentDetails: {
          environment: Environment.Prod,
        },
        dataClassesDetails: {
          dataClasses: [DataClass.Pii],
        },
      },
      backupStatus: BackupStatus.Protected,
      tags: { Env: 'Prod' },
      subnets: ['10.0.0.0/8'],
      latestSnapshotTime: new Date(new Date(now).setHours(now.getHours() - 20)),
      discoveredTime: new Date(new Date(now).setHours(now.getHours() - 20)),
      sourceStorage: {
        filesCount: 0,
        sizeBytes: convertToBytes(1024, 'GiB'),
        volumeCount: 10,
      },
      snapshotStorage: {
        sizeBytes: convertToBytes(2100, 'GB'),
        eonSnapshotCount: 0,
        nonEonSnapshotCount: 0,
      },
      id: '',
      region: '',
      backupPolicies: [],
      securityAssignmentStatus: SecurityAssignmentStatus.SECURITY_ASSIGNED,
      vaults: [],
      providerAccountId: '',
      cloudProvider: Provider.ProviderUnspecified,
      providerResourceId: '',
    },
    {
      resourceName: 'linux-server-2025',
      resourceType:
        VirtualMachinesResourceTypes[
          secondaryCloudProviders[2 % secondaryCloudProviders.length]
        ],
      classifications: {
        environmentDetails: {
          environment: Environment.Prod,
        },
        dataClassesDetails: {
          dataClasses: [DataClass.Fi, DataClass.Pii],
        },
      },
      backupStatus: BackupStatus.Protected,
      tags: {},
      subnets: ['255.255.0.0'],
      latestSnapshotTime: new Date(new Date(now).setHours(now.getHours() - 11)),
      discoveredTime: new Date(new Date(now).setHours(now.getHours() - 11)),
      sourceStorage: {
        filesCount: 34_000,
        sizeBytes: convertToBytes(500, 'GiB'),
        volumeCount: 5,
      },
      snapshotStorage: {
        sizeBytes: convertToBytes(1200, 'GB'),
        eonSnapshotCount: 0,
        nonEonSnapshotCount: 6,
      },
      securityScan: {
        snapshotId: '',
        conclusion: SecurityScanConclusionEnum.HighChanceForRansomware,
        totalEvidenceCount: 3,
        createdTime: dayjs().subtract(1, 'day').toDate(),
      },
      id: '',
      region: '',
      backupPolicies: [],
      securityAssignmentStatus: SecurityAssignmentStatus.SECURITY_ASSIGNED,
      vaults: [],
      providerAccountId: '',
      cloudProvider: Provider.ProviderUnspecified,
      providerResourceId: '',
    },
    {
      resourceName: 'Bookings-dev-web-2',
      resourceType:
        VirtualMachinesResourceTypes[
          secondaryCloudProviders[0 % secondaryCloudProviders.length]
        ],
      backupStatus: BackupStatus.NotBackedUp,
      tags: { Env: 'Dev', Web: '' },
      subnets: ['10.0.0.0/8'],
      latestSnapshotTime: new Date(new Date(now).setHours(now.getHours() - 12)),
      discoveredTime: new Date(new Date(now).setHours(now.getHours() - 12)),
      sourceStorage: {
        filesCount: 20_000,
        sizeBytes: convertToBytes(80, 'GiB'),
        volumeCount: 1,
      },
      snapshotStorage: {
        sizeBytes: convertToBytes(230, 'GB'),
        eonSnapshotCount: 30,
        nonEonSnapshotCount: 0,
      },
      id: '',
      region: '',
      backupPolicies: [],
      securityAssignmentStatus: SecurityAssignmentStatus.SECURITY_ASSIGNED,
      vaults: [],
      providerAccountId: '',
      cloudProvider: Provider.ProviderUnspecified,
      providerResourceId: '',
    },
    {
      resourceName: 'db-server-2',
      resourceType:
        VirtualMachinesResourceTypes[
          secondaryCloudProviders[0 % secondaryCloudProviders.length]
        ],
      classifications: {
        environmentDetails: {
          environment: Environment.Prod,
        },
        appsDetails: {
          apps: [
            {
              name: 'MongoDB',
            },
          ],
        },
        dataClassesDetails: {
          dataClasses: [DataClass.Pii],
        },
      },
      backupStatus: BackupStatus.Protected,
      tags: {},
      subnets: ['10.0.0.0/8'],
      latestSnapshotTime: new Date(new Date(now).setHours(now.getHours() - 4)),
      discoveredTime: new Date(new Date(now).setHours(now.getHours() - 4)),
      sourceStorage: {
        filesCount: 1_200_000,
        sizeBytes: convertToBytes(500, 'GiB'),
        volumeCount: 4,
      },
      snapshotStorage: {
        sizeBytes: convertToBytes(726, 'GB'),
        eonSnapshotCount: 0,
        nonEonSnapshotCount: 12,
      },
      securityScan: {
        snapshotId: '',
        conclusion: SecurityScanConclusionEnum.HighChanceForRansomware,
        totalEvidenceCount: 3,
        createdTime: dayjs().subtract(1, 'day').toDate(),
      },
      id: '',
      region: '',
      backupPolicies: [],
      securityAssignmentStatus: SecurityAssignmentStatus.SECURITY_ASSIGNED,
      vaults: [],
      providerAccountId: '',
      cloudProvider: Provider.ProviderUnspecified,
      providerResourceId: '',
    },
    {
      resourceName: 'prod-api-cluster',
      resourceType: KubernetesResourceTypes[demoSettings.primaryProvider],
      backupStatus: BackupStatus.Protected,
      classifications: {
        environmentDetails: {
          environment: Environment.Prod,
        },
      },
      resourceProperties: {
        awsEksNamespace: {
          secretsCount: 25,
          clusterName: 'production-cluster',
          name: 'prod-namespace',
          status: 'Active',
          creationTime: new Date('2025-02-20T14:20:48Z'),
          persistentVolumeClaims: [
            {
              providerResourceId: '',
              path: 'production-cluster-dynamic-pvc-d4b71706-8aae-4083-918d-af2d0242251a',
              availabilityZone: 'us-east-1b',
              volumeSettings: {
                type: 'gp3',
                iops: 3000,
                throughput: 125,
                sizeBytes: 2147483648,
              },
              fsType: 'ext4',
              pvName: 'pvc-d4b71706-8aae-4083-918d-af2d0242251a',
              pvcName: 'prod-pvc',
              accessModes: [KubernetesAccessMode.ReadWriteOnce],
            },
            {
              providerResourceId: '',
              path: 'production-cluster-dynamic-pvc-2a979978-f4ac-41e8-bb2b-eed354291900',
              availabilityZone: 'us-east-1b',
              volumeSettings: {
                type: 'gp3',
                iops: 3000,
                throughput: 125,
                sizeBytes: 1073741824,
              },
              fsType: 'ext4',
              pvName: 'pvc-2a979978-f4ac-41e8-bb2b-eed354291900',
              pvcName: 'prod-pvc-1',
              accessModes: [KubernetesAccessMode.ReadWriteOnce],
            },
            {
              providerResourceId: '',
              path: 'production-cluster-dynamic-pvc-cf2d9b48-23fc-47a8-a27f-4e3ff9348466',
              availabilityZone: 'us-east-1b',
              volumeSettings: {
                type: 'gp3',
                iops: 3000,
                throughput: 125,
                sizeBytes: 2147483648,
              },
              fsType: 'ext4',
              pvName: 'pvc-cf2d9b48-23fc-47a8-a27f-4e3ff9348466',
              pvcName: 'prod-pvc-2',
              accessModes: [KubernetesAccessMode.ReadWriteOnce],
            },
            {
              providerResourceId: '',
              path: 'production-cluster-dynamic-pvc-7b37f18d-5955-4987-b98b-5074647af366',
              availabilityZone: 'us-east-1b',
              volumeSettings: {
                type: 'gp3',
                iops: 3000,
                throughput: 125,
                sizeBytes: 3221225472,
              },
              fsType: 'ext4',
              pvName: 'pvc-7b37f18d-5955-4987-b98b-5074647af366',
              pvcName: 'prod-pvc-3',
              accessModes: [KubernetesAccessMode.ReadWriteOnce],
            },
          ],
          labels: {
            'kubernetes.io/metadata.name': 'prod-namespace',
          },
          resourceVersion: '9189',
        },
        gcpGkeNamespace: {
          secretsCount: 25,
          clusterName: 'production-cluster',
          name: 'prod-namespace',
          status: 'Active',
          creationTime: new Date('2025-02-20T14:20:48Z'),
          persistentVolumeClaims: [
            {
              providerResourceId: '',
              path: 'production-cluster-dynamic-pvc-d4b71706-8aae-4083-918d-af2d0242251a',
              availabilityZone: '',
              volumeSettings: {
                type: '',
                sizeBytes: 2147483648,
              },
              fsType: '',
              pvName: 'pvc-d4b71706-8aae-4083-918d-af2d0242251a',
              pvcName: 'prod-pvc',
              accessModes: [KubernetesAccessMode.ReadWriteOnce],
            },
            {
              providerResourceId: '',
              path: 'production-cluster-dynamic-pvc-2a979978-f4ac-41e8-bb2b-eed354291900',
              availabilityZone: '',
              volumeSettings: {
                type: '',
                sizeBytes: 1073741824,
              },
              fsType: '',
              pvName: 'pvc-2a979978-f4ac-41e8-bb2b-eed354291900',
              pvcName: 'prod-pvc-1',
              accessModes: [KubernetesAccessMode.ReadWriteOnce],
            },
            {
              providerResourceId: '',
              path: 'production-cluster-dynamic-pvc-cf2d9b48-23fc-47a8-a27f-4e3ff9348466',
              availabilityZone: '',
              volumeSettings: {
                type: '',
                sizeBytes: 2147483648,
              },
              fsType: '',
              pvName: 'pvc-cf2d9b48-23fc-47a8-a27f-4e3ff9348466',
              pvcName: 'prod-pvc-2',
              accessModes: [KubernetesAccessMode.ReadWriteOnce],
            },
            {
              providerResourceId: '',
              path: 'production-cluster-dynamic-pvc-7b37f18d-5955-4987-b98b-5074647af366',
              availabilityZone: '',
              volumeSettings: {
                type: '',
                sizeBytes: 3221225472,
              },
              fsType: '',
              pvName: 'pvc-7b37f18d-5955-4987-b98b-5074647af366',
              pvcName: 'prod-pvc-3',
              accessModes: [KubernetesAccessMode.ReadWriteOnce],
            },
          ],
          labels: {
            'kubernetes.io/metadata.name': 'prod-namespace',
          },
          resourceVersion: '9189',
        },
        azureAksNamespace: {
          secretsCount: 25,
          clusterName: 'production-cluster',
          name: 'prod-namespace',
          status: 'Active',
          creationTime: new Date('2025-02-20T14:20:48Z'),
          persistentVolumeClaims: [
            {
              providerResourceId: '',
              path: 'production-cluster-dynamic-pvc-d4b71706-8aae-4083-918d-af2d0242251a',
              availabilityZone: '',
              volumeSettings: {
                type: '',
                sizeBytes: 2147483648,
              },
              fsType: '',
              pvName: 'pvc-d4b71706-8aae-4083-918d-af2d0242251a',
              pvcName: 'prod-pvc',
              accessModes: [KubernetesAccessMode.ReadWriteOnce],
            },
            {
              providerResourceId: '',
              path: 'production-cluster-dynamic-pvc-2a979978-f4ac-41e8-bb2b-eed354291900',
              availabilityZone: '',
              volumeSettings: {
                type: '',
                sizeBytes: 1073741824,
              },
              fsType: '',
              pvName: 'pvc-2a979978-f4ac-41e8-bb2b-eed354291900',
              pvcName: 'prod-pvc-1',
              accessModes: [KubernetesAccessMode.ReadWriteOnce],
            },
            {
              providerResourceId: '',
              path: 'production-cluster-dynamic-pvc-cf2d9b48-23fc-47a8-a27f-4e3ff9348466',
              availabilityZone: '',
              volumeSettings: {
                type: '',
                sizeBytes: 2147483648,
              },
              fsType: '',
              pvName: 'pvc-cf2d9b48-23fc-47a8-a27f-4e3ff9348466',
              pvcName: 'prod-pvc-2',
              accessModes: [KubernetesAccessMode.ReadWriteOnce],
            },
            {
              providerResourceId: '',
              path: 'production-cluster-dynamic-pvc-7b37f18d-5955-4987-b98b-5074647af366',
              availabilityZone: '',
              volumeSettings: {
                type: '',
                sizeBytes: 3221225472,
              },
              fsType: '',
              pvName: 'pvc-7b37f18d-5955-4987-b98b-5074647af366',
              pvcName: 'prod-pvc-3',
              accessModes: [KubernetesAccessMode.ReadWriteOnce],
            },
          ],
          labels: {
            'kubernetes.io/metadata.name': 'prod-namespace',
          },
          resourceVersion: '9189',
        },
      },
      tags: { environment: 'production', service: 'api-services' },
      subnets: ['10.1.0.0/16'],
      latestSnapshotTime: new Date(new Date(now).setHours(now.getHours() - 2)),
      discoveredTime: new Date(new Date(now).setHours(now.getHours() - 2)),
      sourceStorage: {
        filesCount: 0,
        sizeBytes: convertToBytes(250, 'GiB'),
        volumeCount: 12,
      },
      snapshotStorage: {
        sizeBytes: convertToBytes(300, 'GB'),
        eonSnapshotCount: 24,
        nonEonSnapshotCount: 0,
      },
      id: '',
      region: '',
      backupPolicies: [],
      securityAssignmentStatus: SecurityAssignmentStatus.SECURITY_ASSIGNED,
      vaults: [],
      providerAccountId: '',
      cloudProvider: Provider.ProviderUnspecified,
      providerResourceId: '',
    },
    {
      resourceName: 'Insurance-2',
      resourceType:
        ManagedSQLResourceTypes[
          secondaryCloudProviders[0 % secondaryCloudProviders.length]
        ],
      classifications: {
        environmentDetails: {
          environment: Environment.Prod,
        },
        appsDetails: {
          apps: [
            {
              name: 'PostgreSQL',
            },
          ],
        },
        dataClassesDetails: {
          dataClasses: [DataClass.Fi, DataClass.Pii],
        },
      },
      backupStatus: BackupStatus.Protected,
      tags: { Env: 'Prod' },
      subnets: ['10.0.0.0/8'],
      latestSnapshotTime: new Date(new Date(now).setHours(now.getHours() - 20)),
      discoveredTime: new Date(new Date(now).setHours(now.getHours() - 20)),
      sourceStorage: {
        filesCount: 0,
        sizeBytes: convertToBytes(1024, 'GiB'),
        volumeCount: 10,
      },
      snapshotStorage: {
        sizeBytes: convertToBytes(2100, 'GB'),
        eonSnapshotCount: 0,
        nonEonSnapshotCount: 0,
      },
      resourceProperties: {
        awsRds: {
          sizeBytes: convertToBytes(1_000, 'GB'),
          clusterId: 'cluster-1',
          engine: 'postgres',
          engineVersion: '13.3',
          storageSettings: {
            type: 'gp2',
            iops: 100,
            throughput: 343,
          },
          databaseName: 'workflows_db',
          instanceClass: 'db.t3.micro',
          isMultiAZ: false,
          isPubliclyAccessible: true,
        },
      },
      securityScan: {
        snapshotId: '',
        conclusion: SecurityScanConclusionEnum.HighChanceForRansomware,
        totalEvidenceCount: 3,
        createdTime: dayjs().subtract(1, 'day').toDate(),
      },
      id: '',
      region: '',
      backupPolicies: [],
      securityAssignmentStatus: SecurityAssignmentStatus.SECURITY_ASSIGNED,
      vaults: [],
      providerAccountId: '',
      cloudProvider: Provider.ProviderUnspecified,
      providerResourceId: '',
    },
    {
      resourceName: 'Catalog-2',
      resourceType:
        VirtualMachinesResourceTypes[
          secondaryCloudProviders[0 % secondaryCloudProviders.length]
        ],
      classifications: {
        environmentDetails: {
          environment: Environment.Prod,
        },
        appsDetails: {
          apps: [
            {
              name: 'PostgreSQL',
              paths: ['/var/lib/postgresql/data'],
            },
          ],
        },
        dataClassesDetails: {
          dataClasses: [DataClass.Fi, DataClass.Pii],
        },
      },
      backupStatus: BackupStatus.Protected,
      tags: { Env: 'Prod' },
      subnets: ['10.0.0.0/8'],
      latestSnapshotTime: new Date(new Date(now).setHours(now.getHours() - 20)),
      discoveredTime: new Date(new Date(now).setHours(now.getHours() - 20)),
      sourceStorage: {
        filesCount: 0,
        sizeBytes: convertToBytes(1024, 'GiB'),
        volumeCount: 10,
      },
      snapshotStorage: {
        sizeBytes: convertToBytes(2100, 'GB'),
        eonSnapshotCount: 0,
        nonEonSnapshotCount: 0,
      },
      resourceProperties: {
        awsRds: {
          sizeBytes: convertToBytes(1_000, 'GB'),
          clusterId: 'cluster-1',
          engine: 'postgres',
          engineVersion: '13.3',
          storageSettings: {
            type: 'gp2',
            iops: 100,
            throughput: 343,
          },
          databaseName: 'workflows_db',
          instanceClass: 'db.t3.micro',
          isMultiAZ: false,
          isPubliclyAccessible: true,
        },
      },
      id: '',
      region: '',
      backupPolicies: [],
      securityAssignmentStatus: SecurityAssignmentStatus.SECURITY_ASSIGNED,
      vaults: [],
      providerAccountId: '',
      cloudProvider: Provider.ProviderUnspecified,
      providerResourceId: '',
    },
    {
      resourceName: 'FinApp-prod-web-2',
      resourceType:
        VirtualMachinesResourceTypes[
          secondaryCloudProviders[0 % secondaryCloudProviders.length]
        ],
      classifications: {
        environmentDetails: {
          environment: Environment.Prod,
        },
        appsDetails: {
          apps: [
            {
              name: 'Apache Tomcat',
            },
          ],
        },
        dataClassesDetails: {
          dataClasses: [DataClass.Fi, DataClass.Pii],
        },
      },
      backupStatus: BackupStatus.Protected,
      tags: { Env: 'Prod', Web: '' },
      subnets: ['10.0.0.0/8'],
      latestSnapshotTime: new Date(new Date(now).setHours(now.getHours() - 3)),
      discoveredTime: new Date(new Date(now).setHours(now.getHours() - 3)),
      sourceStorage: {
        filesCount: 3_200,
        sizeBytes: convertToBytes(100, 'GiB'),
        volumeCount: 1,
      },
      snapshotStorage: {
        sizeBytes: convertToBytes(400, 'GB'),
        eonSnapshotCount: 200,
        nonEonSnapshotCount: 22,
      },
      securityScan: {
        snapshotId: '',
        conclusion: SecurityScanConclusionEnum.HighChanceForRansomware,
        totalEvidenceCount: 3,
        createdTime: dayjs().subtract(1, 'day').toDate(),
      },
      id: '',
      region: '',
      backupPolicies: [],
      securityAssignmentStatus: SecurityAssignmentStatus.SECURITY_ASSIGNED,
      vaults: [],
      providerAccountId: '',
      cloudProvider: Provider.ProviderUnspecified,
      providerResourceId: '',
    },
    {
      resourceName: '-',
      resourceType:
        VirtualMachinesResourceTypes[
          secondaryCloudProviders[0 % secondaryCloudProviders.length]
        ],
      backupStatus: BackupStatus.Protected,
      classifications: {
        environmentDetails: {
          environment: Environment.ProdInternal,
        },
        appsDetails: {
          apps: [
            {
              name: 'Cassandra',
            },
          ],
        },
      },
      tags: { Env: 'Internal production' },
      subnets: ['10.0.0.0/8'],
      latestSnapshotTime: new Date(new Date(now).setHours(now.getHours() - 11)),
      discoveredTime: new Date(new Date(now).setHours(now.getHours() - 11)),
      sourceStorage: {
        filesCount: 10_000,
        sizeBytes: convertToBytes(150, 'GiB'),
        volumeCount: 2,
      },
      snapshotStorage: {
        sizeBytes: convertToBytes(155, 'GB'),
        eonSnapshotCount: 0,
        nonEonSnapshotCount: 3,
      },
      id: '',
      region: '',
      backupPolicies: [],
      securityAssignmentStatus: SecurityAssignmentStatus.SECURITY_ASSIGNED,
      vaults: [],
      providerAccountId: '',
      cloudProvider: Provider.ProviderUnspecified,
      providerResourceId: '',
    },
    {
      resourceName: 'Insurance-3',
      resourceType: ResourceType.AtlasMongodbCluster,
      classifications: {
        environmentDetails: {
          environment: Environment.Prod,
        },
        appsDetails: {
          apps: [
            {
              name: 'MongoDB',
            },
          ],
        },
        dataClassesDetails: {
          dataClasses: [DataClass.Pii],
        },
      },
      backupStatus: BackupStatus.Protected,
      tags: { Env: 'Prod' },
      latestSnapshotTime: new Date(new Date(now).setHours(now.getHours() - 55)),
      discoveredTime: new Date(new Date(now).setHours(now.getHours() - 55)),
      sourceStorage: {
        filesCount: 0,
        sizeBytes: convertToBytes(1024, 'GiB'),
        volumeCount: 0,
      },
      snapshotStorage: {
        sizeBytes: convertToBytes(2100, 'GB'),
        eonSnapshotCount: 0,
        nonEonSnapshotCount: 21,
      },
      resourceProperties: {
        awsRds: {
          sizeBytes: convertToBytes(1_000, 'GB'),
          clusterId: 'cluster-1',
          engine: 'Mongo',
          engineVersion: '6.3',
          storageSettings: {
            type: 'm3',
            iops: 100,
            throughput: 343,
          },
          databaseName: 'Videos',
          instanceClass: 'm3',
          isMultiAZ: false,
          isPubliclyAccessible: true,
        },
      },
      id: '',
      region: '',
      backupPolicies: [],
      securityAssignmentStatus: SecurityAssignmentStatus.SECURITY_ASSIGNED,
      vaults: [],
      providerAccountId: '',
      cloudProvider: Provider.ProviderUnspecified,
      providerResourceId: '',
    },
    {
      resourceName: 'kube-namespace',
      resourceType:
        KubernetesResourceTypes[
          secondaryCloudProviders[0 % secondaryCloudProviders.length]
        ],
      backupStatus: BackupStatus.Protected,
      classifications: {
        environmentDetails: {
          environment: Environment.Prod,
        },
      },
      tags: { Env: 'Prod' },
      subnets: ['10.0.0.0/8'],
      latestSnapshotTime: new Date(new Date(now).setHours(now.getHours() - 9)),
      discoveredTime: new Date(new Date(now).setHours(now.getHours() - 9)),
      sourceStorage: {
        filesCount: 13_000,
        sizeBytes: convertToBytes(100, 'GiB'),
        volumeCount: 1,
      },
      snapshotStorage: {
        sizeBytes: convertToBytes(233, 'GB'),
        eonSnapshotCount: 0,
        nonEonSnapshotCount: 0,
      },
      resourceProperties: {
        awsEksNamespace: {
          secretsCount: 25,
          clusterName: 'production-cluster',
          name: 'namespace',
          status: 'Active',
          creationTime: new Date('2025-02-20T14:20:48Z'),
          persistentVolumeClaims: [
            {
              providerResourceId: '',
              path: 'production-cluster-dynamic-pvc-d4b71706-8aae-4083-918d-af2d0242251a',
              availabilityZone: 'us-east-1b',
              volumeSettings: {
                type: 'gp3',
                sizeBytes: 2147483648,
                iops: 3000,
                throughput: 125,
              },
              fsType: 'ext4',
              pvName: 'pvc-d4b71706-8aae-4083-918d-af2d0242251a',
              pvcName: 'prod-pvc',
              accessModes: [KubernetesAccessMode.ReadWriteOnce],
            },
            {
              providerResourceId: '',
              path: 'production-cluster-dynamic-pvc-2a979978-f4ac-41e8-bb2b-eed354291900',
              availabilityZone: 'us-east-1b',
              volumeSettings: {
                type: 'gp2',
                sizeBytes: 1073741824,
                iops: 100,
              },
              fsType: 'ext4',
              pvName: 'pvc-2a979978-f4ac-41e8-bb2b-eed354291900',
              pvcName: 'prod-pvc-1',
              accessModes: [KubernetesAccessMode.ReadWriteOnce],
            },
            {
              providerResourceId: '',
              path: 'production-cluster-dynamic-pvc-cf2d9b48-23fc-47a8-a27f-4e3ff9348466',
              availabilityZone: 'us-east-1b',
              volumeSettings: {
                type: 'gp2',
                sizeBytes: 2147483648,
                iops: 100,
              },
              fsType: 'ext4',
              pvName: 'pvc-cf2d9b48-23fc-47a8-a27f-4e3ff9348466',
              pvcName: 'prod-pvc-2',
              accessModes: [KubernetesAccessMode.ReadWriteOnce],
            },
            {
              providerResourceId: '',
              path: 'production-cluster-dynamic-pvc-7b37f18d-5955-4987-b98b-5074647af366',
              availabilityZone: 'us-east-1b',
              volumeSettings: {
                type: 'gp3',
                sizeBytes: 3221225472,
                iops: 3000,
                throughput: 125,
              },
              fsType: 'ext4',
              pvName: 'pvc-7b37f18d-5955-4987-b98b-5074647af366',
              pvcName: 'prod-pvc-3',
              accessModes: [KubernetesAccessMode.ReadWriteOnce],
            },
          ],
          labels: {
            'kubernetes.io/metadata.name': 'prod-namespace',
          },
          resourceVersion: '9189',
        },
        gcpGkeNamespace: {
          secretsCount: 25,
          clusterName: 'production-cluster',
          name: 'namespace',
          status: 'Active',
          creationTime: new Date('2025-02-20T14:20:48Z'),
          persistentVolumeClaims: [
            {
              providerResourceId: '',
              path: 'production-cluster-dynamic-pvc-d4b71706-8aae-4083-918d-af2d0242251a',
              availabilityZone: '',
              volumeSettings: {
                type: '',
                sizeBytes: 2147483648,
              },
              fsType: '',
              pvName: 'pvc-d4b71706-8aae-4083-918d-af2d0242251a',
              pvcName: 'prod-pvc',
              accessModes: [KubernetesAccessMode.ReadWriteOnce],
            },
            {
              providerResourceId: '',
              path: 'production-cluster-dynamic-pvc-2a979978-f4ac-41e8-bb2b-eed354291900',
              availabilityZone: '',
              volumeSettings: {
                type: '',
                sizeBytes: 1073741824,
              },
              fsType: '',
              pvName: 'pvc-2a979978-f4ac-41e8-bb2b-eed354291900',
              pvcName: 'prod-pvc-1',
              accessModes: [KubernetesAccessMode.ReadWriteOnce],
            },
            {
              providerResourceId: '',
              path: 'production-cluster-dynamic-pvc-cf2d9b48-23fc-47a8-a27f-4e3ff9348466',
              availabilityZone: '',
              volumeSettings: {
                type: '',
                sizeBytes: 2147483648,
              },
              fsType: '',
              pvName: 'pvc-cf2d9b48-23fc-47a8-a27f-4e3ff9348466',
              pvcName: 'prod-pvc-2',
              accessModes: [KubernetesAccessMode.ReadWriteOnce],
            },
            {
              providerResourceId: '',
              path: 'production-cluster-dynamic-pvc-7b37f18d-5955-4987-b98b-5074647af366',
              availabilityZone: '',
              volumeSettings: {
                type: '',
                sizeBytes: 3221225472,
              },
              fsType: '',
              pvName: 'pvc-7b37f18d-5955-4987-b98b-5074647af366',
              pvcName: 'prod-pvc-3',
              accessModes: [KubernetesAccessMode.ReadWriteOnce],
            },
          ],
          labels: {
            'kubernetes.io/metadata.name': 'prod-namespace',
          },
          resourceVersion: '9189',
        },
        azureAksNamespace: {
          secretsCount: 25,
          clusterName: 'production-cluster',
          name: 'namespace',
          status: 'Active',
          creationTime: new Date('2025-02-20T14:20:48Z'),
          persistentVolumeClaims: [
            {
              providerResourceId: '',
              path: 'production-cluster-dynamic-pvc-d4b71706-8aae-4083-918d-af2d0242251a',
              availabilityZone: '',
              volumeSettings: {
                type: '',
                sizeBytes: 2147483648,
              },
              fsType: '',
              pvName: 'pvc-d4b71706-8aae-4083-918d-af2d0242251a',
              pvcName: 'prod-pvc',
              accessModes: [KubernetesAccessMode.ReadWriteOnce],
            },
            {
              providerResourceId: '',
              path: 'production-cluster-dynamic-pvc-2a979978-f4ac-41e8-bb2b-eed354291900',
              availabilityZone: '',
              volumeSettings: {
                type: '',
                sizeBytes: 1073741824,
              },
              fsType: '',
              pvName: 'pvc-2a979978-f4ac-41e8-bb2b-eed354291900',
              pvcName: 'prod-pvc-1',
              accessModes: [KubernetesAccessMode.ReadWriteOnce],
            },
            {
              providerResourceId: '',
              path: 'production-cluster-dynamic-pvc-cf2d9b48-23fc-47a8-a27f-4e3ff9348466',
              availabilityZone: '',
              volumeSettings: {
                type: '',
                sizeBytes: 2147483648,
              },
              fsType: '',
              pvName: 'pvc-cf2d9b48-23fc-47a8-a27f-4e3ff9348466',
              pvcName: 'prod-pvc-2',
              accessModes: [KubernetesAccessMode.ReadWriteOnce],
            },
            {
              providerResourceId: '',
              path: 'production-cluster-dynamic-pvc-7b37f18d-5955-4987-b98b-5074647af366',
              availabilityZone: '',
              volumeSettings: {
                type: '',
                sizeBytes: 3221225472,
              },
              fsType: '',
              pvName: 'pvc-7b37f18d-5955-4987-b98b-5074647af366',
              pvcName: 'prod-pvc-3',
              accessModes: [KubernetesAccessMode.ReadWriteOnce],
            },
          ],
          labels: {
            'kubernetes.io/metadata.name': 'prod-namespace',
          },
          resourceVersion: '9189',
        },
      },
      id: '',
      region: '',
      backupPolicies: [],
      securityAssignmentStatus: SecurityAssignmentStatus.SECURITY_ASSIGNED,
      vaults: [],
      providerAccountId: '',
      cloudProvider: Provider.ProviderUnspecified,
      providerResourceId: '',
    },
    {
      resourceName: 'Catalog-3',
      resourceType: ResourceType.AtlasMongodbCluster,
      classifications: {
        environmentDetails: {
          environment: Environment.Prod,
        },
        appsDetails: {
          apps: [
            {
              name: 'MongoDB',
            },
          ],
        },
        dataClassesDetails: {
          dataClasses: [DataClass.Pii],
        },
      },
      backupStatus: BackupStatus.Protected,
      tags: { Env: 'Prod' },
      latestSnapshotTime: new Date(new Date(now).setHours(now.getHours() - 55)),
      discoveredTime: new Date(new Date(now).setHours(now.getHours() - 55)),
      sourceStorage: {
        filesCount: 0,
        sizeBytes: convertToBytes(1024, 'GiB'),
        volumeCount: 0,
      },
      snapshotStorage: {
        sizeBytes: convertToBytes(2100, 'GB'),
        eonSnapshotCount: 0,
        nonEonSnapshotCount: 21,
      },
      resourceProperties: {
        awsRds: {
          sizeBytes: convertToBytes(1_000, 'GB'),
          clusterId: 'cluster-1',
          engine: 'Mongo',
          engineVersion: '6.3',
          storageSettings: {
            type: 'm3',
            iops: 100,
            throughput: 343,
          },
          databaseName: 'Videos',
          instanceClass: 'm3',
          isMultiAZ: false,
          isPubliclyAccessible: true,
        },
      },
      id: '',
      region: '',
      backupPolicies: [],
      securityAssignmentStatus: SecurityAssignmentStatus.SECURITY_ASSIGNED,
      vaults: [],
      providerAccountId: '',
      cloudProvider: Provider.ProviderUnspecified,
      providerResourceId: '',
    },
    {
      resourceName: 'alpha-east-bucket',
      resourceType:
        StorageResourceTypes[
          secondaryCloudProviders[0 % secondaryCloudProviders.length]
        ],
      backupStatus: BackupStatus.Protected,
      tags: { Env: 'Internal production' },
      subnets: ['10.0.0.0/8'],
      classifications: {
        environmentDetails: {
          environment: Environment.ProdInternal,
        },
      },
      latestSnapshotTime: new Date(new Date(now).setHours(now.getHours() - 20)),
      discoveredTime: new Date(new Date(now).setHours(now.getHours() - 20)),
      sourceStorage: {
        filesCount: 1_200_000,
        sizeBytes: convertToBytes(50, 'TiB'),
        volumeCount: 0,
      },
      snapshotStorage: {
        sizeBytes: convertToBytes(7.5, 'TB'),
        eonSnapshotCount: 50,
        nonEonSnapshotCount: 5,
      },
      id: '',
      region: '',
      backupPolicies: [],
      securityAssignmentStatus: SecurityAssignmentStatus.SECURITY_ASSIGNED,
      vaults: [],
      providerAccountId: '',
      cloudProvider: Provider.ProviderUnspecified,
      providerResourceId: '',
    },
    {
      resourceName: 'Insurance-1',
      resourceType:
        ManagedSQLResourceTypes[
          secondaryCloudProviders[0 % secondaryCloudProviders.length]
        ],
      classifications: {
        environmentDetails: {
          environment: Environment.Prod,
        },
        appsDetails: {
          apps: [
            {
              name: 'MySQL',
            },
          ],
        },
      },
      backupStatus: BackupStatus.Protected,
      tags: { Env: 'Prod' },
      subnets: ['10.0.0.0/8'],
      latestSnapshotTime: new Date(new Date(now).setHours(now.getHours() - 11)),
      discoveredTime: new Date(new Date(now).setHours(now.getHours() - 11)),
      sourceStorage: {
        filesCount: 0,
        sizeBytes: convertToBytes(400, 'GiB'),
        volumeCount: 4,
      },
      snapshotStorage: {
        sizeBytes: convertToBytes(500, 'GB'),
        eonSnapshotCount: 0,
        nonEonSnapshotCount: 0,
      },
      id: '',
      region: '',
      backupPolicies: [],
      securityAssignmentStatus: SecurityAssignmentStatus.SECURITY_ASSIGNED,
      vaults: [],
      providerAccountId: '',
      cloudProvider: Provider.ProviderUnspecified,
      providerResourceId: '',
    },
    {
      resourceName: 'Catalog-1',
      resourceType:
        ManagedSQLResourceTypes[
          secondaryCloudProviders[0 % secondaryCloudProviders.length]
        ],

      classifications: {
        appsDetails: {
          apps: [{ name: 'MySQL' }],
        },
        environmentDetails: {
          environment: Environment.Prod,
        },
      },
      backupStatus: BackupStatus.Protected,
      tags: { Env: 'Prod' },
      subnets: ['10.0.0.0/8'],
      latestSnapshotTime: new Date(new Date(now).setHours(now.getHours() - 11)),
      discoveredTime: new Date(new Date(now).setHours(now.getHours() - 11)),
      sourceStorage: {
        filesCount: 0,
        sizeBytes: convertToBytes(400, 'GiB'),
        volumeCount: 4,
      },
      snapshotStorage: {
        sizeBytes: convertToBytes(500, 'GB'),
        eonSnapshotCount: 0,
        nonEonSnapshotCount: 0,
      },
      id: '',
      region: '',
      backupPolicies: [],
      securityAssignmentStatus: SecurityAssignmentStatus.SECURITY_ASSIGNED,
      vaults: [],
      providerAccountId: '',
      cloudProvider: Provider.ProviderUnspecified,
      providerResourceId: '',
    },
    {
      resourceName: 'FinApp-dev-DB-replica',
      resourceType:
        ManagedSQLResourceTypes[
          secondaryCloudProviders[0 % secondaryCloudProviders.length]
        ],
      classifications: {
        environmentDetails: {
          environment: Environment.ProdInternal,
        },
        appsDetails: {
          apps: [
            {
              name: 'MySQL',
            },
          ],
        },
      },
      backupStatus: BackupStatus.Protected,
      tags: { Env: 'Internal production' },
      subnets: ['10.0.0.0/8'],
      sourceStorage: {
        filesCount: 0,
        sizeBytes: convertToBytes(400, 'GiB'),
        volumeCount: 2,
      },
      snapshotStorage: {
        sizeBytes: 0,
        eonSnapshotCount: 0,
        nonEonSnapshotCount: 0,
      },
      id: '',
      region: '',
      backupPolicies: [],
      securityAssignmentStatus: SecurityAssignmentStatus.SECURITY_ASSIGNED,
      vaults: [],
      providerAccountId: '',
      cloudProvider: Provider.ProviderUnspecified,
      providerResourceId: '',
    },
    {
      resourceName: 'MyTestBucket',
      resourceType:
        StorageResourceTypes[
          secondaryCloudProviders[0 % secondaryCloudProviders.length]
        ],
      backupStatus: BackupStatus.ExcludedFromBackup,
      tags: {},
      subnets: ['10.0.0.0/8'],
      sourceStorage: {
        filesCount: 12_100_000,
        sizeBytes: convertToBytes(20, 'GiB'),
        volumeCount: 0,
      },
      snapshotStorage: {
        sizeBytes: 0,
        eonSnapshotCount: 0,
        nonEonSnapshotCount: 21,
      },
      id: '',
      region: '',
      backupPolicies: [],
      securityAssignmentStatus: SecurityAssignmentStatus.SECURITY_ASSIGNED,
      vaults: [],
      providerAccountId: '',
      cloudProvider: Provider.ProviderUnspecified,
      providerResourceId: '',
    },
    {
      resourceName: 'assets',
      resourceType:
        StorageResourceTypes[
          secondaryCloudProviders[0 % secondaryCloudProviders.length]
        ],
      classifications: {
        environmentDetails: {
          environment: Environment.Prod,
        },
      },
      backupStatus: BackupStatus.Protected,
      tags: { Env: 'Prod' },
      subnets: ['10.0.0.0/8'],
      latestSnapshotTime: new Date(new Date(now).setHours(now.getHours() - 8)),
      discoveredTime: new Date(new Date(now).setHours(now.getHours() - 8)),
      sourceStorage: {
        filesCount: 1_200,
        sizeBytes: convertToBytes(50, 'GiB'),
        volumeCount: 0,
      },
      snapshotStorage: {
        sizeBytes: convertToBytes(70, 'GB'),
        eonSnapshotCount: 0,
        nonEonSnapshotCount: 0,
      },
      id: '',
      region: '',
      backupPolicies: [],
      securityAssignmentStatus: SecurityAssignmentStatus.SECURITY_ASSIGNED,
      vaults: [],
      providerAccountId: '',
      cloudProvider: Provider.ProviderUnspecified,
      providerResourceId: '',
    },
    {
      resourceName: '-',
      resourceType: ResourceType.AwsEfs,
      classifications: {
        environmentDetails: {
          environment: Environment.ProdInternal,
        },
      },
      backupStatus: BackupStatus.Protected,
      tags: { Env: 'Internal production' },
      subnets: ['10.0.0.0/8'],
      latestSnapshotTime: new Date(new Date(now).setHours(now.getHours() - 8)),
      discoveredTime: new Date(new Date(now).setHours(now.getHours() - 8)),
      sourceStorage: {
        filesCount: 12_100_000,
        sizeBytes: convertToBytes(250, 'GiB'),
        volumeCount: 0,
      },
      snapshotStorage: {
        sizeBytes: convertToBytes(300, 'GB'),
        eonSnapshotCount: 0,
        nonEonSnapshotCount: 0,
      },
      id: '',
      region: '',
      backupPolicies: [],
      securityAssignmentStatus: SecurityAssignmentStatus.SECURITY_ASSIGNED,
      vaults: [],
      providerAccountId: '',
      cloudProvider: Provider.ProviderUnspecified,
      providerResourceId: '',
    },
    {
      resourceName: 'xmotion-db',
      resourceType:
        VirtualMachinesResourceTypes[
          secondaryCloudProviders[0 % secondaryCloudProviders.length]
        ],
      backupStatus: BackupStatus.ViolationsDetected,
      classifications: {
        environmentDetails: {
          environment: Environment.Prod,
        },
        appsDetails: {
          apps: [
            {
              name: 'PostgreSQL',
            },
          ],
        },
      },
      tags: { Env: 'Prod' },
      subnets: ['10.0.0.0/8'],
      latestSnapshotTime: new Date(new Date(now).setHours(now.getHours() - 8)),
      discoveredTime: new Date(new Date(now).setHours(now.getHours() - 8)),
      sourceStorage: {
        filesCount: 35_000,
        sizeBytes: convertToBytes(500, 'GiB'),
        volumeCount: 4,
      },
      snapshotStorage: {
        sizeBytes: convertToBytes(600, 'GB'),
        eonSnapshotCount: 100,
        nonEonSnapshotCount: 11,
      },
      controlViolationCounts: {
        high: 1,
        medium: 1,
        low: 2,
      },
      id: '',
      region: '',
      backupPolicies: [],
      securityAssignmentStatus: SecurityAssignmentStatus.SECURITY_ASSIGNED,
      vaults: [],
      providerAccountId: '',
      cloudProvider: Provider.ProviderUnspecified,
      providerResourceId: '',
    },
    {
      resourceName: 'xmotion-db',
      resourceType:
        VirtualMachinesResourceTypes[
          secondaryCloudProviders[0 % secondaryCloudProviders.length]
        ],
      classifications: {
        environmentDetails: {
          environment: Environment.Prod,
        },
        appsDetails: {
          apps: [
            {
              name: 'PostgreSQL',
            },
          ],
        },
        dataClassesDetails: {
          dataClasses: [DataClass.Pii],
        },
      },
      backupStatus: BackupStatus.Protected,
      tags: { Env: 'Prod' },
      subnets: ['10.0.0.0/8'],
      latestSnapshotTime: new Date(new Date(now).setHours(now.getHours() - 11)),
      discoveredTime: new Date(new Date(now).setHours(now.getHours() - 11)),
      sourceStorage: {
        filesCount: 35_000,
        sizeBytes: convertToBytes(500, 'GiB'),
        volumeCount: 3,
      },
      snapshotStorage: {
        sizeBytes: convertToBytes(630, 'GB'),
        eonSnapshotCount: 0,
        nonEonSnapshotCount: 4,
      },
      id: '',
      region: '',
      backupPolicies: [],
      securityAssignmentStatus: SecurityAssignmentStatus.SECURITY_ASSIGNED,
      vaults: [],
      providerAccountId: '',
      cloudProvider: Provider.ProviderUnspecified,
      providerResourceId: '',
    },
    {
      resourceName: 'xmotion-search',
      resourceType:
        VirtualMachinesResourceTypes[
          secondaryCloudProviders[0 % secondaryCloudProviders.length]
        ],
      classifications: {
        environmentDetails: {
          environment: Environment.Prod,
        },
        appsDetails: {
          apps: [
            {
              name: 'Elasticsearch',
            },
          ],
        },
        dataClassesDetails: {
          dataClasses: [DataClass.Pii],
        },
      },
      backupStatus: BackupStatus.Protected,
      tags: { Env: 'Prod' },
      subnets: ['10.0.0.0/8'],
      latestSnapshotTime: new Date(new Date(now).setHours(now.getHours() - 3)),
      discoveredTime: new Date(new Date(now).setHours(now.getHours() - 3)),
      sourceStorage: {
        filesCount: 736_000,
        sizeBytes: convertToBytes(600, 'GiB'),
        volumeCount: 3,
      },
      snapshotStorage: {
        sizeBytes: convertToBytes(892, 'GB'),
        eonSnapshotCount: 150,
        nonEonSnapshotCount: 12,
      },
      id: '',
      region: '',
      backupPolicies: [],
      securityAssignmentStatus: SecurityAssignmentStatus.SECURITY_ASSIGNED,
      vaults: [],
      providerAccountId: '',
      cloudProvider: Provider.ProviderUnspecified,
      providerResourceId: '',
    },
    {
      resourceName: 'i-2588275',
      resourceType:
        VirtualMachinesResourceTypes[
          secondaryCloudProviders[0 % secondaryCloudProviders.length]
        ],
      classifications: {
        environmentDetails: {
          environment: Environment.Prod,
        },
        appsDetails: {
          apps: [
            {
              name: 'SAP Application Server',
            },
          ],
        },
        dataClassesDetails: {
          dataClasses: [DataClass.Pii],
        },
      },
      backupStatus: BackupStatus.NotBackedUp,
      tags: { Env: 'Prod' },
      subnets: ['10.0.0.0/8'],
      latestSnapshotTime: new Date(new Date(now).setHours(now.getHours() - 4)),
      discoveredTime: new Date(new Date(now).setHours(now.getHours() - 4)),
      sourceStorage: {
        filesCount: 54_300,
        sizeBytes: convertToBytes(4096, 'GiB'),
        volumeCount: 12,
      },
      snapshotStorage: {
        sizeBytes: 0,
        eonSnapshotCount: 4_000,
        nonEonSnapshotCount: 10,
      },
      id: '',
      region: '',
      backupPolicies: [],
      securityAssignmentStatus: SecurityAssignmentStatus.SECURITY_ASSIGNED,
      vaults: [],
      providerAccountId: '',
      cloudProvider: Provider.ProviderUnspecified,
      providerResourceId: '',
    },
    {
      resourceName: 'i-1765234',
      resourceType:
        VirtualMachinesResourceTypes[
          secondaryCloudProviders[0 % secondaryCloudProviders.length]
        ],
      classifications: {
        environmentDetails: {
          environment: Environment.ProdInternal,
        },
        appsDetails: {
          apps: [
            {
              name: 'SAP Application Server',
            },
          ],
        },
      },
      backupStatus: BackupStatus.Protected,
      tags: { Env: 'Internal production' },
      subnets: ['10.0.0.0/8'],
      latestSnapshotTime: new Date(new Date(now).setHours(now.getHours() - 11)),
      discoveredTime: new Date(new Date(now).setHours(now.getHours() - 11)),
      sourceStorage: {
        filesCount: 6_500,
        sizeBytes: convertToBytes(50, 'GiB'),
        volumeCount: 1,
      },
      snapshotStorage: {
        sizeBytes: convertToBytes(45, 'GB'),
        eonSnapshotCount: 0,
        nonEonSnapshotCount: 7,
      },
      id: '',
      region: '',
      backupPolicies: [],
      securityAssignmentStatus: SecurityAssignmentStatus.SECURITY_ASSIGNED,
      vaults: [],
      providerAccountId: '',
      cloudProvider: Provider.ProviderUnspecified,
      providerResourceId: '',
    },
    {
      resourceName: 'large-files',
      resourceType: ResourceType.AzureFileShare,
      classifications: {
        environmentDetails: {
          environment: Environment.Prod,
        },
        dataClassesDetails: {
          dataClasses: [DataClass.Pii],
        },
      },
      backupStatus: BackupStatus.Protected,
      tags: {},
      subnets: ['10.0.0.0/8'],
      latestSnapshotTime: new Date(new Date(now).setHours(now.getHours() - 11)),
      discoveredTime: new Date(new Date(now).setHours(now.getHours() - 11)),
      sourceStorage: {
        filesCount: 758_200,
        sizeBytes: convertToBytes(50, 'TiB'),
        volumeCount: 0,
      },
      snapshotStorage: {
        sizeBytes: convertToBytes(62_000, 'GB'),
        eonSnapshotCount: 0,
        nonEonSnapshotCount: 15,
      },
      id: '',
      region: '',
      backupPolicies: [],
      securityAssignmentStatus: SecurityAssignmentStatus.SECURITY_ASSIGNED,
      vaults: [],
      providerAccountId: '',
      cloudProvider: Provider.ProviderUnspecified,
      providerResourceId: '',
    },
    {
      resourceName: 'db-server-1',
      resourceType:
        VirtualMachinesResourceTypes[
          secondaryCloudProviders[0 % secondaryCloudProviders.length]
        ],
      classifications: {
        environmentDetails: {
          environment: Environment.Prod,
        },
        appsDetails: {
          apps: [
            {
              name: 'PostgreSQL',
            },
          ],
        },
        dataClassesDetails: {
          dataClasses: [DataClass.Fi, DataClass.Pii],
        },
      },
      backupStatus: BackupStatus.Protected,
      tags: {},
      subnets: ['10.0.0.0/8'],
      latestSnapshotTime: new Date(new Date(now).setHours(now.getHours() - 11)),
      discoveredTime: new Date(new Date(now).setHours(now.getHours() - 11)),
      sourceStorage: {
        filesCount: 34_000,
        sizeBytes: convertToBytes(500, 'GiB'),
        volumeCount: 5,
      },
      snapshotStorage: {
        sizeBytes: convertToBytes(1200, 'GB'),
        eonSnapshotCount: 0,
        nonEonSnapshotCount: 6,
      },
      securityScan: {
        snapshotId: '',
        conclusion: SecurityScanConclusionEnum.HighChanceForRansomware,
        totalEvidenceCount: 3,
        createdTime: dayjs().subtract(1, 'day').toDate(),
      },
      id: '',
      region: '',
      backupPolicies: [],
      securityAssignmentStatus: SecurityAssignmentStatus.SECURITY_ASSIGNED,
      vaults: [],
      providerAccountId: '',
      cloudProvider: Provider.ProviderUnspecified,
      providerResourceId: '',
    },
    {
      backupStatus: BackupStatus.Protected,
      resourceName: 'restoredest',
      resourceType:
        StorageResourceTypes[
          secondaryCloudProviders[1 % secondaryCloudProviders.length]
        ],
      classifications: {
        dataClassesDetails: {
          dataClasses: [DataClass.Fi, DataClass.Pii],
          eonDataClassifications: [DataClass.Fi, DataClass.Pii],
        },
        environmentDetails: {
          environment: Environment.Prod,
        },
      },
      latestSnapshotTime: new Date(new Date(now).setHours(now.getHours() - 2)),
      discoveredTime: new Date(new Date(now).setHours(now.getHours() - 2)),
      tags: {},
      snapshotStorage: {
        sizeBytes: 540672,
        eonSnapshotCount: 4,
        nonEonSnapshotCount: 19,
      },
      sourceStorage: {
        sizeBytes: 1280091810,
      },
      id: '',
      region: '',
      backupPolicies: [],
      securityAssignmentStatus: SecurityAssignmentStatus.SECURITY_ASSIGNED,
      vaults: [],
      providerAccountId: '',
      cloudProvider: Provider.ProviderUnspecified,
      providerResourceId: '',
    },
    {
      resourceName: 'Invoices',
      resourceType: ResourceType.AwsEfs,
      classifications: {
        environmentDetails: {
          environment: Environment.Prod,
        },
        dataClassesDetails: {
          dataClasses: [DataClass.Fi, DataClass.Pii],
        },
      },
      backupStatus: BackupStatus.Protected,
      tags: { Env: 'Prod' },
      subnets: ['10.0.0.0/8'],
      latestSnapshotTime: new Date(new Date(now).setHours(now.getHours() - 20)),
      discoveredTime: new Date(new Date(now).setHours(now.getHours() - 20)),
      sourceStorage: {
        filesCount: 75_200,
        sizeBytes: convertToBytes(70, 'TiB'),
        volumeCount: 0,
      },
      snapshotStorage: {
        sizeBytes: convertToBytes(180_000, 'GB'),
        eonSnapshotCount: 0,
        nonEonSnapshotCount: 0,
      },
      id: '',
      region: '',
      backupPolicies: [],
      securityAssignmentStatus: SecurityAssignmentStatus.SECURITY_ASSIGNED,
      vaults: [],
      providerAccountId: '',
      cloudProvider: Provider.ProviderUnspecified,
      providerResourceId: '',
    },
    {
      resourceName: 'db-server-3',
      resourceType:
        VirtualMachinesResourceTypes[
          secondaryCloudProviders[1 % secondaryCloudProviders.length]
        ],
      classifications: {
        environmentDetails: {
          environment: Environment.Prod,
        },
        appsDetails: {
          apps: [
            {
              name: 'MongoDB',
            },
          ],
        },
        dataClassesDetails: {
          dataClasses: [DataClass.Pii],
        },
      },
      backupStatus: BackupStatus.Protected,
      tags: {},
      subnets: ['10.0.0.0/8'],
      latestSnapshotTime: new Date(new Date(now).setHours(now.getHours() - 4)),
      discoveredTime: new Date(new Date(now).setHours(now.getHours() - 4)),
      sourceStorage: {
        filesCount: 1_400_000,
        sizeBytes: convertToBytes(500, 'GiB'),
        volumeCount: 4,
      },
      snapshotStorage: {
        sizeBytes: convertToBytes(612, 'GB'),
        eonSnapshotCount: 0,
        nonEonSnapshotCount: 3,
      },
      id: '',
      region: '',
      backupPolicies: [],
      securityAssignmentStatus: SecurityAssignmentStatus.SECURITY_ASSIGNED,
      vaults: [],
      providerAccountId: '',
      cloudProvider: Provider.ProviderUnspecified,
      providerResourceId: '',
    },
    {
      resourceName: 'dc-1',
      resourceType:
        VirtualMachinesResourceTypes[
          secondaryCloudProviders[1 % secondaryCloudProviders.length]
        ],
      classifications: {
        environmentDetails: {
          environment: Environment.ProdInternal,
        },
        appsDetails: {
          apps: [
            {
              name: 'Active Directory',
            },
          ],
        },
        dataClassesDetails: {
          dataClasses: [DataClass.Pii],
        },
      },
      backupStatus: BackupStatus.Protected,
      tags: {},
      subnets: ['10.0.0.0/8'],
      latestSnapshotTime: new Date(new Date(now).setHours(now.getHours() - 4)),
      discoveredTime: new Date(new Date(now).setHours(now.getHours() - 4)),
      sourceStorage: {
        filesCount: 1_200,
        sizeBytes: convertToBytes(100, 'GiB'),
        volumeCount: 2,
      },
      snapshotStorage: {
        sizeBytes: convertToBytes(180, 'GB'),
        eonSnapshotCount: 0,
        nonEonSnapshotCount: 17,
      },
      id: '',
      region: '',
      backupPolicies: [],
      securityAssignmentStatus: SecurityAssignmentStatus.SECURITY_ASSIGNED,
      vaults: [],
      providerAccountId: '',
      cloudProvider: Provider.ProviderUnspecified,
      providerResourceId: '',
    },
    {
      resourceName: 'dc-2',
      resourceType:
        VirtualMachinesResourceTypes[
          secondaryCloudProviders[1 % secondaryCloudProviders.length]
        ],
      classifications: {
        environmentDetails: {
          environment: Environment.Prod,
        },
        appsDetails: {
          apps: [
            {
              name: 'Active Directory',
            },
          ],
        },
        dataClassesDetails: {
          dataClasses: [DataClass.Pii],
        },
      },
      backupStatus: BackupStatus.Protected,
      tags: { net1: '' },
      subnets: ['10.0.0.0/8'],
      latestSnapshotTime: new Date(new Date(now).setHours(now.getHours() - 11)),
      discoveredTime: new Date(new Date(now).setHours(now.getHours() - 11)),
      sourceStorage: {
        filesCount: 1_100,
        sizeBytes: convertToBytes(100, 'GiB'),
        volumeCount: 1,
      },
      snapshotStorage: {
        sizeBytes: convertToBytes(180, 'GB'),
        eonSnapshotCount: 0,
        nonEonSnapshotCount: 8,
      },
      securityScan: {
        snapshotId: '',
        conclusion: SecurityScanConclusionEnum.HighChanceForRansomware,
        totalEvidenceCount: 3,
        createdTime: dayjs().subtract(1, 'day').toDate(),
      },
      id: '',
      region: '',
      backupPolicies: [],
      securityAssignmentStatus: SecurityAssignmentStatus.SECURITY_ASSIGNED,
      vaults: [],
      providerAccountId: '',
      cloudProvider: Provider.ProviderUnspecified,
      providerResourceId: '',
    },
    {
      resourceName: 'app-server-2',
      resourceType:
        VirtualMachinesResourceTypes[
          secondaryCloudProviders[1 % secondaryCloudProviders.length]
        ],
      classifications: {
        environmentDetails: {
          environment: Environment.Prod,
        },
        appsDetails: {
          apps: [
            {
              name: 'Redis',
            },
            {
              name: 'Apache Kafka',
            },
          ],
        },
        dataClassesDetails: {
          dataClasses: [DataClass.Pii],
        },
      },
      backupStatus: BackupStatus.NotBackedUp,
      tags: {},
      subnets: ['10.0.0.0/8'],
      sourceStorage: {
        filesCount: 45_200,
        sizeBytes: convertToBytes(350, 'GiB'),
        volumeCount: 5,
      },
      snapshotStorage: {
        sizeBytes: 0,
        eonSnapshotCount: 0,
        nonEonSnapshotCount: 4,
      },
      id: '',
      region: '',
      backupPolicies: [],
      securityAssignmentStatus: SecurityAssignmentStatus.SECURITY_ASSIGNED,
      vaults: [],
      providerAccountId: '',
      cloudProvider: Provider.ProviderUnspecified,
      providerResourceId: '',
    },
    {
      resourceName: 'web-server-1',
      resourceType:
        VirtualMachinesResourceTypes[
          secondaryCloudProviders[1 % secondaryCloudProviders.length]
        ],
      classifications: {
        environmentDetails: {
          environment: Environment.Prod,
        },
        appsDetails: {
          apps: [
            {
              name: 'IIS',
            },
          ],
        },
      },
      backupStatus: BackupStatus.Protected,
      tags: {},
      subnets: ['10.0.0.0/8'],
      latestSnapshotTime: new Date(new Date(now).setHours(now.getHours() - 8)),
      discoveredTime: new Date(new Date(now).setHours(now.getHours() - 8)),
      sourceStorage: {
        filesCount: 31_200,
        sizeBytes: convertToBytes(200, 'GiB'),
        volumeCount: 1,
      },
      snapshotStorage: {
        sizeBytes: convertToBytes(243, 'GB'),
        eonSnapshotCount: 0,
        nonEonSnapshotCount: 5,
      },
      id: '',
      region: '',
      backupPolicies: [],
      securityAssignmentStatus: SecurityAssignmentStatus.SECURITY_ASSIGNED,
      vaults: [],
      providerAccountId: '',
      cloudProvider: Provider.ProviderUnspecified,
      providerResourceId: '',
    },
    {
      resourceName: 'tickets-server-2',
      resourceType:
        VirtualMachinesResourceTypes[
          secondaryCloudProviders[1 % secondaryCloudProviders.length]
        ],
      classifications: {
        environmentDetails: {
          environment: Environment.Prod,
        },
        appsDetails: {
          apps: [
            {
              name: 'Jira',
            },
            {
              name: 'Kibana',
            },
          ],
        },
        dataClassesDetails: {
          dataClasses: [DataClass.Fi, DataClass.Pii],
        },
      },
      backupStatus: BackupStatus.Protected,
      tags: {
        Env: 'Production',
        Web: '',
        Name: 'e2e resource',
        'eon:eon-account': 'e74d0174-d282-4920-b6e0-aed4d4f6ce02',
        'eon:test-id': 'test_e2e_851725187588_c765b0bd',
        'eon:test-name': '',
      },
      subnets: ['10.0.0.0/8'],
      latestSnapshotTime: new Date(new Date(now).setHours(now.getHours() - 8)),
      discoveredTime: new Date(new Date(now).setHours(now.getHours() - 8)),
      sourceStorage: {
        filesCount: 42_300,
        sizeBytes: convertToBytes(200, 'GiB'),
        volumeCount: 2,
      },
      snapshotStorage: {
        sizeBytes: convertToBytes(312, 'GB'),
        eonSnapshotCount: 0,
        nonEonSnapshotCount: 10,
      },
      id: '',
      region: '',
      backupPolicies: [],
      securityAssignmentStatus: SecurityAssignmentStatus.SECURITY_ASSIGNED,
      vaults: [],
      providerAccountId: '',
      cloudProvider: Provider.ProviderUnspecified,
      providerResourceId: '',
    },
    {
      resourceName: 'analytics-1',
      resourceType:
        VirtualMachinesResourceTypes[
          secondaryCloudProviders[0 % secondaryCloudProviders.length]
        ],
      classifications: {
        environmentDetails: {
          environment: Environment.Prod,
        },
        appsDetails: {
          apps: [
            {
              name: 'Grafana',
            },
            {
              name: 'Elasticsearch',
            },
          ],
        },
      },
      backupStatus: BackupStatus.Protected,
      tags: { Env: 'Prod', Web: '' },
      subnets: ['10.0.0.0/8'],
      latestSnapshotTime: new Date(new Date(now).setHours(now.getHours() - 8)),
      discoveredTime: new Date(new Date(now).setHours(now.getHours() - 8)),
      sourceStorage: {
        filesCount: 77_000,
        sizeBytes: convertToBytes(300, 'GiB'),
        volumeCount: 2,
      },
      snapshotStorage: {
        sizeBytes: convertToBytes(350, 'GB'),
        eonSnapshotCount: 0,
        nonEonSnapshotCount: 29,
      },
      securityScan: {
        snapshotId: '',
        conclusion: SecurityScanConclusionEnum.HighChanceForRansomware,
        totalEvidenceCount: 3,
        createdTime: dayjs().subtract(1, 'day').toDate(),
      },
      id: '',
      region: '',
      backupPolicies: [],
      securityAssignmentStatus: SecurityAssignmentStatus.SECURITY_ASSIGNED,
      vaults: [],
      providerAccountId: '',
      cloudProvider: Provider.ProviderUnspecified,
      providerResourceId: '',
    },
    {
      resourceName: 'analytics-2',
      resourceType:
        VirtualMachinesResourceTypes[
          secondaryCloudProviders[0 % secondaryCloudProviders.length]
        ],
      backupStatus: BackupStatus.Protected,
      classifications: {
        environmentDetails: {
          environment: Environment.ProdInternal,
        },
        appsDetails: {
          apps: [
            {
              name: 'Grafana',
            },
          ],
        },
      },
      tags: { Env: 'Internal production', Web: '' },
      subnets: ['10.0.0.0/8'],
      latestSnapshotTime: new Date(new Date(now).setHours(now.getHours() - 8)),
      discoveredTime: new Date(new Date(now).setHours(now.getHours() - 8)),
      sourceStorage: {
        filesCount: 54_300,
        sizeBytes: convertToBytes(300, 'GiB'),
        volumeCount: 2,
      },
      snapshotStorage: {
        sizeBytes: convertToBytes(412, 'GB'),
        eonSnapshotCount: 100,
        nonEonSnapshotCount: 9,
      },
      id: '',
      region: '',
      backupPolicies: [],
      securityAssignmentStatus: SecurityAssignmentStatus.SECURITY_ASSIGNED,
      vaults: [],
      providerAccountId: '',
      cloudProvider: Provider.ProviderUnspecified,
      providerResourceId: '',
    },
    {
      resourceName: 'app-server-1',
      resourceType:
        VirtualMachinesResourceTypes[
          secondaryCloudProviders[0 % secondaryCloudProviders.length]
        ],
      backupStatus: BackupStatus.Protected,
      classifications: {
        environmentDetails: {
          environment: Environment.ProdInternal,
        },
        appsDetails: {
          apps: [
            {
              name: 'Redis',
            },
          ],
        },
      },
      tags: { Env: 'Internal production' },
      subnets: ['10.0.0.0/8'],
      latestSnapshotTime: new Date(new Date(now).setHours(now.getHours() - 8)),
      discoveredTime: new Date(new Date(now).setHours(now.getHours() - 8)),
      sourceStorage: {
        filesCount: 43_000_000,
        sizeBytes: convertToBytes(250, 'GiB'),
        volumeCount: 3,
      },
      snapshotStorage: {
        sizeBytes: convertToBytes(276, 'GB'),
        eonSnapshotCount: 0,
        nonEonSnapshotCount: 6,
      },
      id: '',
      region: '',
      backupPolicies: [],
      securityAssignmentStatus: SecurityAssignmentStatus.SECURITY_ASSIGNED,
      vaults: [],
      providerAccountId: '',
      cloudProvider: Provider.ProviderUnspecified,
      providerResourceId: '',
    },
    {
      resourceName: 'db-server-4',
      resourceType:
        VirtualMachinesResourceTypes[
          secondaryCloudProviders[0 % secondaryCloudProviders.length]
        ],
      backupStatus: BackupStatus.Protected,
      classifications: {
        environmentDetails: {
          environment: Environment.ProdInternal,
        },
        appsDetails: {
          apps: [
            {
              name: 'PostgreSQL',
              paths: ['/eon/var/lib/postgresql/16/main'],
            },
          ],
        },
      },
      tags: { Env: 'Internal production' },
      subnets: ['10.0.0.0/8'],
      latestSnapshotTime: new Date(new Date(now).setHours(now.getHours() - 8)),
      discoveredTime: new Date(new Date(now).setHours(now.getHours() - 8)),
      sourceStorage: {
        filesCount: 773_200,
        sizeBytes: convertToBytes(250, 'GiB'),
        volumeCount: 2,
      },
      snapshotStorage: {
        sizeBytes: convertToBytes(324, 'GB'),
        eonSnapshotCount: 0,
        nonEonSnapshotCount: 14,
      },
      id: '',
      region: '',
      backupPolicies: [],
      securityAssignmentStatus: SecurityAssignmentStatus.SECURITY_ASSIGNED,
      vaults: [],
      providerAccountId: '',
      cloudProvider: Provider.ProviderUnspecified,
      providerResourceId: '',
    },
    {
      resourceName: 'web-server-1',
      resourceType:
        VirtualMachinesResourceTypes[
          secondaryCloudProviders[0 % secondaryCloudProviders.length]
        ],
      backupStatus: BackupStatus.Protected,
      classifications: {
        environmentDetails: {
          environment: Environment.ProdInternal,
        },
        appsDetails: {
          apps: [
            {
              name: 'IIS',
            },
          ],
        },
      },
      tags: { Env: 'Internal production', Web: '' },
      subnets: ['10.0.0.0/8'],
      latestSnapshotTime: new Date(new Date(now).setHours(now.getHours() - 9)),
      discoveredTime: new Date(new Date(now).setHours(now.getHours() - 9)),
      sourceStorage: {
        filesCount: 13_000,
        sizeBytes: convertToBytes(100, 'GiB'),
        volumeCount: 1,
      },
      snapshotStorage: {
        sizeBytes: convertToBytes(233, 'GB'),
        eonSnapshotCount: 0,
        nonEonSnapshotCount: 8,
      },
      id: '',
      region: '',
      backupPolicies: [],
      securityAssignmentStatus: SecurityAssignmentStatus.SECURITY_ASSIGNED,
      vaults: [],
      providerAccountId: '',
      cloudProvider: Provider.ProviderUnspecified,
      providerResourceId: '',
    },
    {
      backupStatus: BackupStatus.Protected,
      resourceName: 'mssql-server/dangling-db',
      resourceType: ResourceType.AzureSqlDatabase,
      classifications: {
        dataClassesDetails: {
          dataClasses: [DataClass.Pii],
          eonDataClassifications: [DataClass.Pii],
        },
        environmentDetails: {
          environment: Environment.Prod,
        },
        appsDetails: {
          apps: [
            {
              name: 'MSSQLServer',
            },
          ],
        },
      },
      latestSnapshotTime: new Date(new Date(now).setHours(now.getHours() - 8)),
      discoveredTime: new Date(new Date(now).setHours(now.getHours() - 8)),
      tags: {},
      snapshotStorage: {
        eonSnapshotCount: 4,
        nonEonSnapshotCount: 25,
      },
      createdTime: new Date('2025-02-08T23:33:52.019304Z'),
      sourceStorage: {
        sizeBytes: 34359738368,
      },
      id: '',
      region: '',
      backupPolicies: [],
      securityAssignmentStatus: SecurityAssignmentStatus.SECURITY_ASSIGNED,
      vaults: [],
      providerAccountId: '',
      cloudProvider: Provider.ProviderUnspecified,
      providerResourceId: '',
    },
    {
      backupStatus: BackupStatus.ExcludedFromBackup,
      resourceName: 'windows-vm',
      resourceType:
        VirtualMachinesResourceTypes[
          secondaryCloudProviders[1 % secondaryCloudProviders.length]
        ],
      classifications: {
        dataClassesDetails: {
          dataClasses: [DataClass.Fi, DataClass.Pii],
          eonDataClassifications: [DataClass.Fi, DataClass.Pii],
        },
        environmentDetails: {
          environment: Environment.Stage,
        },
        appsDetails: {
          apps: [
            {
              name: 'Nginx',
            },
          ],
        },
      },
      latestSnapshotTime: new Date(new Date(now).setHours(now.getHours() - 5)),
      discoveredTime: new Date(new Date(now).setHours(now.getHours() - 5)),
      tags: {},
      snapshotStorage: {
        sizeBytes: 2982150144,
        eonSnapshotCount: 3,
        nonEonSnapshotCount: 14,
      },
      sourceStorage: {
        sizeBytes: 136367308800,
      },
      id: '',
      region: '',
      backupPolicies: [],
      securityAssignmentStatus: SecurityAssignmentStatus.SECURITY_ASSIGNED,
      vaults: [],
      providerAccountId: '',
      cloudProvider: Provider.ProviderUnspecified,
      providerResourceId: '',
    },
    {
      backupStatus: BackupStatus.ExcludedFromBackup,
      resourceName: 'windows-smalldisk',
      resourceType:
        VirtualMachinesResourceTypes[
          secondaryCloudProviders[1 % secondaryCloudProviders.length]
        ],
      classifications: {
        environmentDetails: {
          environment: Environment.Stage,
        },
        appsDetails: {
          apps: [
            {
              name: 'Internet Information Services (IIS)',
            },
          ],
        },
      },
      tags: {},
      snapshotStorage: {
        sizeBytes: 9261023232,
        eonSnapshotCount: 4,
        nonEonSnapshotCount: 5,
      },
      latestSnapshotTime: new Date(new Date(now).setHours(now.getHours() - 3)),
      discoveredTime: new Date(new Date(now).setHours(now.getHours() - 3)),
      sourceStorage: {
        sizeBytes: 32214351872,
      },
      id: '',
      region: '',
      backupPolicies: [],
      securityAssignmentStatus: SecurityAssignmentStatus.SECURITY_ASSIGNED,
      vaults: [],
      providerAccountId: '',
      cloudProvider: Provider.ProviderUnspecified,
      providerResourceId: '',
    },
    {
      backupStatus: BackupStatus.ViolationsDetected,
      resourceName: 'test-plan',
      resourceType:
        VirtualMachinesResourceTypes[
          secondaryCloudProviders[1 % secondaryCloudProviders.length]
        ],
      classifications: {
        environmentDetails: {
          environment: Environment.Prod,
        },
      },
      latestSnapshotTime: new Date(new Date(now).setHours(now.getHours() - 7)),
      discoveredTime: new Date(new Date(now).setHours(now.getHours() - 7)),
      tags: {},
      snapshotStorage: {
        sizeBytes: 33179702272,
        eonSnapshotCount: 1,
        nonEonSnapshotCount: 13,
      },
      sourceStorage: {
        sizeBytes: 32213303296,
      },
      controlViolationCounts: {
        high: 1,
        medium: 1,
        low: 2,
      },
      id: '',
      region: '',
      backupPolicies: [],
      securityAssignmentStatus: SecurityAssignmentStatus.SECURITY_ASSIGNED,
      vaults: [],
      providerAccountId: '',
      cloudProvider: Provider.ProviderUnspecified,
      providerResourceId: '',
    },
    {
      backupStatus: BackupStatus.Protected,
      resourceName: 'mysql-11',
      resourceType: ResourceType.AzureMysql,
      classifications: {
        environmentDetails: {
          environment: Environment.Prod,
        },
        appsDetails: {
          apps: [
            {
              name: 'MySQL',
            },
          ],
        },
      },
      latestSnapshotTime: new Date(new Date(now).setHours(now.getHours() - 10)),
      discoveredTime: new Date(new Date(now).setHours(now.getHours() - 10)),
      tags: {},
      snapshotStorage: {},
      sourceStorage: {
        sizeBytes: 32213303296,
      },
      id: '',
      region: '',
      backupPolicies: [],
      securityAssignmentStatus: SecurityAssignmentStatus.SECURITY_ASSIGNED,
      vaults: [],
      providerAccountId: '',
      cloudProvider: Provider.ProviderUnspecified,
      providerResourceId: '',
    },
    {
      backupStatus: BackupStatus.Protected,
      resourceName: 'summary-4',
      resourceType:
        StorageResourceTypes[
          secondaryCloudProviders[1 % secondaryCloudProviders.length]
        ],
      classifications: {
        dataClassesDetails: {
          dataClasses: [DataClass.Fi, DataClass.Pii],
          eonDataClassifications: [DataClass.Fi, DataClass.Pii],
        },
        environmentDetails: {
          environment: Environment.Prod,
        },
      },
      latestSnapshotTime: new Date(new Date(now).setHours(now.getHours() - 12)),
      discoveredTime: new Date(new Date(now).setHours(now.getHours() - 12)),
      tags: {},
      snapshotStorage: {
        sizeBytes: 3611648,
        eonSnapshotCount: 4,
      },
      sourceStorage: {
        sizeBytes: 2514403,
      },
      id: '',
      region: '',
      backupPolicies: [],
      securityAssignmentStatus: SecurityAssignmentStatus.SECURITY_ASSIGNED,
      vaults: [],
      providerAccountId: '',
      cloudProvider: Provider.ProviderUnspecified,
      providerResourceId: '',
    },
    {
      backupStatus: BackupStatus.Protected,
      resourceName: 'Sql Server Managed Internal Prod 1',
      resourceType:
        ManagedSQLResourceTypes[
          secondaryCloudProviders[1 % secondaryCloudProviders.length]
        ],
      classifications: {
        environmentDetails: {
          environment: Environment.ProdInternal,
        },
        appsDetails: {
          apps: [
            {
              name: 'MSSQLServer',
            },
          ],
        },
      },
      latestSnapshotTime: new Date(new Date(now).setHours(now.getHours() - 3)),
      discoveredTime: new Date(new Date(now).setHours(now.getHours() - 3)),
      tags: {
        Env: 'prod-internal',
        'prod-internal': '',
      },
      snapshotStorage: {},
      sourceStorage: {
        sizeBytes: 34359738368,
      },
      id: '',
      region: '',
      backupPolicies: [],
      securityAssignmentStatus: SecurityAssignmentStatus.SECURITY_ASSIGNED,
      vaults: [],
      providerAccountId: '',
      cloudProvider: Provider.ProviderUnspecified,
      providerResourceId: '',
    },
    {
      backupStatus: BackupStatus.Protected,
      resourceName: 'Postgres',
      resourceType:
        VirtualMachinesResourceTypes[
          secondaryCloudProviders[1 % secondaryCloudProviders.length]
        ],
      classifications: {
        dataClassesDetails: {
          dataClasses: [DataClass.Fi, DataClass.Pii],
          eonDataClassifications: [DataClass.Fi, DataClass.Pii],
        },
        environmentDetails: {
          environment: Environment.Prod,
        },
        appsDetails: {
          apps: [
            {
              name: 'Microsoft SQL Server',
              paths: ['/eon/var/lib/mssql/16/main'],
            },
          ],
        },
      },
      latestSnapshotTime: new Date(new Date(now).setHours(now.getHours() - 1)),
      discoveredTime: new Date(new Date(now).setHours(now.getHours() - 1)),
      tags: {},
      snapshotStorage: {
        sizeBytes: 73904947200,
        eonSnapshotCount: 4,
      },
      sourceStorage: {
        sizeBytes: 17179869184,
      },
      id: '',
      region: '',
      backupPolicies: [],
      securityAssignmentStatus: SecurityAssignmentStatus.SECURITY_ASSIGNED,
      vaults: [],
      providerAccountId: '',
      cloudProvider: Provider.ProviderUnspecified,
      providerResourceId: '',
    },
    {
      backupStatus: BackupStatus.Protected,
      resourceName: 'cross-region',
      resourceType:
        VirtualMachinesResourceTypes[
          secondaryCloudProviders[1 % secondaryCloudProviders.length]
        ],
      classifications: {
        environmentDetails: {
          environment: Environment.Prod,
        },
      },
      latestSnapshotTime: new Date(new Date(now).setHours(now.getHours() - 8)),
      discoveredTime: new Date(new Date(now).setHours(now.getHours() - 8)),
      tags: {},
      snapshotStorage: {
        sizeBytes: 1870397440,
        eonSnapshotCount: 4,
      },
      sourceStorage: {
        sizeBytes: 32213303296,
      },
      id: '',
      region: '',
      backupPolicies: [],
      securityAssignmentStatus: SecurityAssignmentStatus.SECURITY_ASSIGNED,
      vaults: [],
      providerAccountId: '',
      cloudProvider: Provider.ProviderUnspecified,
      providerResourceId: '',
    },
    {
      backupStatus: BackupStatus.Protected,
      resourceName: 'insurance-claims',
      resourceType:
        VirtualMachinesResourceTypes[
          secondaryCloudProviders[1 % secondaryCloudProviders.length]
        ],
      classifications: {
        environmentDetails: {
          environment: Environment.ProdInternal,
        },
        appsDetails: {
          apps: [
            {
              name: 'Microsoft SQL Server',
              paths: ['/var/lib/sql'],
            },
          ],
        },
      },
      latestSnapshotTime: new Date(new Date(now).setHours(now.getHours() - 7)),
      discoveredTime: new Date(new Date(now).setHours(now.getHours() - 7)),
      tags: {},
      snapshotStorage: {},
      sourceStorage: {
        sizeBytes: 2199023255552,
      },
      id: '',
      region: '',
      backupPolicies: [],
      securityAssignmentStatus: SecurityAssignmentStatus.SECURITY_ASSIGNED,
      vaults: [],
      providerAccountId: '',
      cloudProvider: Provider.ProviderUnspecified,
      providerResourceId: '',
    },
    {
      backupStatus: BackupStatus.Protected,
      resourceName: 'imdb11',
      resourceType: ResourceType.AzurePostgresql,
      classifications: {
        environmentDetails: {
          environment: Environment.Prod,
        },
        appsDetails: {
          apps: [
            {
              name: 'PostgreSQL',
            },
          ],
        },
      },
      latestSnapshotTime: new Date(new Date(now).setHours(now.getHours() - 4)),
      discoveredTime: new Date(new Date(now).setHours(now.getHours() - 4)),
      tags: {},
      snapshotStorage: {},
      sourceStorage: {
        sizeBytes: convertToBytes(115, 'GiB'),
      },
      id: '',
      region: '',
      backupPolicies: [],
      securityAssignmentStatus: SecurityAssignmentStatus.SECURITY_ASSIGNED,
      vaults: [],
      providerAccountId: '',
      cloudProvider: Provider.ProviderUnspecified,
      providerResourceId: '',
    },
    {
      backupStatus: BackupStatus.Protected,
      resourceName: 'linux-rockey',
      resourceType:
        VirtualMachinesResourceTypes[
          secondaryCloudProviders[1 % secondaryCloudProviders.length]
        ],
      classifications: {
        environmentDetails: {
          environment: Environment.Prod,
        },
      },
      latestSnapshotTime: new Date(new Date(now).setHours(now.getHours() - 13)),
      discoveredTime: new Date(new Date(now).setHours(now.getHours() - 13)),
      tags: {},
      snapshotStorage: {
        eonSnapshotCount: 1,
      },
      sourceStorage: {
        sizeBytes: 10737418240,
      },
      id: '',
      region: '',
      backupPolicies: [],
      securityAssignmentStatus: SecurityAssignmentStatus.SECURITY_ASSIGNED,
      vaults: [],
      providerAccountId: '',
      cloudProvider: Provider.ProviderUnspecified,
      providerResourceId: '',
    },
    {
      backupStatus: BackupStatus.Protected,
      resourceName: 'analytics-hub',
      resourceType: ResourceType.AzureCosmosdbMongodb,
      classifications: {
        environmentDetails: {
          environment: Environment.Prod,
        },
        appsDetails: {
          apps: [
            {
              name: 'MongoDB',
            },
          ],
        },
      },
      latestSnapshotTime: new Date(new Date(now).setHours(now.getHours() - 7)),
      discoveredTime: new Date(new Date(now).setHours(now.getHours() - 7)),
      tags: {},
      snapshotStorage: {
        eonSnapshotCount: 12,
        nonEonSnapshotCount: 12,
      },
      sourceStorage: {
        sizeBytes: 2199023255552,
      },
      id: '',
      region: '',
      backupPolicies: [],
      securityAssignmentStatus: SecurityAssignmentStatus.SECURITY_ASSIGNED,
      vaults: [],
      providerAccountId: '',
      cloudProvider: Provider.ProviderUnspecified,
      providerResourceId: '',
    },
    {
      backupStatus: BackupStatus.Protected,
      resourceName: 'app-data-store',
      resourceType: ResourceType.AzureCosmosdbNosql,
      classifications: {
        environmentDetails: {
          environment: Environment.Prod,
        },
        dataClassesDetails: {
          dataClasses: [DataClass.Fi, DataClass.Pii],
        },
        appsDetails: {
          apps: [
            {
              name: 'CosmosDB',
            },
          ],
        },
      },
      latestSnapshotTime: new Date(new Date(now).setHours(now.getHours() - 7)),
      discoveredTime: new Date(new Date(now).setHours(now.getHours() - 7)),
      tags: {},
      snapshotStorage: {
        eonSnapshotCount: 41,
        nonEonSnapshotCount: 41,
      },
      sourceStorage: {
        sizeBytes: 3199023255552,
      },
      id: '',
      region: '',
      backupPolicies: [],
      securityAssignmentStatus: SecurityAssignmentStatus.SECURITY_ASSIGNED,
      vaults: [],
      providerAccountId: '',
      cloudProvider: Provider.ProviderUnspecified,
      providerResourceId: '',
    },
    {
      resourceName: 'prod-app-server-1',
      resourceType:
        VirtualMachinesResourceTypes[
          secondaryCloudProviders[2 % secondaryCloudProviders.length]
        ],
      backupStatus: BackupStatus.Protected,
      classifications: {
        environmentDetails: {
          environment: Environment.Prod,
        },
        appsDetails: {
          apps: [
            {
              name: 'SAP Application Server',
            },
            {
              name: 'Redis',
            },
          ],
        },
        dataClassesDetails: {
          dataClasses: [DataClass.Pii],
        },
      },
      tags: { environment: 'production', service: 'app-server' },
      subnets: ['10.0.1.0/24'],
      latestSnapshotTime: new Date(new Date(now).setHours(now.getHours() - 4)),
      discoveredTime: new Date(new Date(now).setHours(now.getHours() - 4)),
      sourceStorage: {
        filesCount: 85000,
        sizeBytes: convertToBytes(120, 'GiB'),
        volumeCount: 2,
      },
      snapshotStorage: {
        sizeBytes: convertToBytes(150, 'GB'),
        eonSnapshotCount: 24,
      },
      id: '',
      region: '',
      backupPolicies: [],
      securityAssignmentStatus: SecurityAssignmentStatus.SECURITY_ASSIGNED,
      vaults: [],
      providerAccountId: '',
      cloudProvider: Provider.ProviderUnspecified,
      providerResourceId: '',
    },
    {
      resourceName: 'prod-mongodb-1',
      resourceType:
        VirtualMachinesResourceTypes[
          secondaryCloudProviders[2 % secondaryCloudProviders.length]
        ],
      backupStatus: BackupStatus.Protected,
      classifications: {
        environmentDetails: {
          environment: Environment.Prod,
        },
        appsDetails: {
          apps: [
            {
              name: 'MongoDB',
              paths: ['/var/lib/mongodb'],
            },
          ],
        },
        dataClassesDetails: {
          dataClasses: [DataClass.Fi, DataClass.Pii],
        },
      },
      tags: { environment: 'production', service: 'database', type: 'mongodb' },
      subnets: ['10.0.2.0/24'],
      latestSnapshotTime: new Date(new Date(now).setHours(now.getHours() - 2)),
      discoveredTime: new Date(new Date(now).setHours(now.getHours() - 2)),
      sourceStorage: {
        filesCount: 250000,
        sizeBytes: convertToBytes(500, 'GiB'),
        volumeCount: 4,
      },
      snapshotStorage: {
        sizeBytes: convertToBytes(600, 'GB'),
        eonSnapshotCount: 48,
      },
      id: '',
      region: '',
      backupPolicies: [],
      securityAssignmentStatus: SecurityAssignmentStatus.SECURITY_ASSIGNED,
      vaults: [],
      providerAccountId: '',
      cloudProvider: Provider.ProviderUnspecified,
      providerResourceId: '',
    },
    {
      resourceName: 'prod-analytics-engine',
      resourceType:
        VirtualMachinesResourceTypes[
          secondaryCloudProviders[2 % secondaryCloudProviders.length]
        ],
      backupStatus: BackupStatus.Protected,
      classifications: {
        environmentDetails: {
          environment: Environment.Prod,
        },
        appsDetails: {
          apps: [
            {
              name: 'Apache Kafka',
            },
            {
              name: 'Kibana',
            },
          ],
        },
      },
      tags: { environment: 'production', service: 'analytics' },
      subnets: ['10.0.3.0/24'],
      latestSnapshotTime: new Date(new Date(now).setHours(now.getHours() - 6)),
      discoveredTime: new Date(new Date(now).setHours(now.getHours() - 6)),
      sourceStorage: {
        filesCount: 150000,
        sizeBytes: convertToBytes(800, 'GiB'),
        volumeCount: 3,
      },
      snapshotStorage: {
        sizeBytes: convertToBytes(900, 'GB'),
        eonSnapshotCount: 36,
        nonEonSnapshotCount: 1,
      },
      id: '',
      region: '',
      backupPolicies: [],
      securityAssignmentStatus: SecurityAssignmentStatus.SECURITY_ASSIGNED,
      vaults: [],
      providerAccountId: '',
      cloudProvider: Provider.ProviderUnspecified,
      providerResourceId: '',
    },
    {
      resourceName: 'internal-monitoring',
      resourceType:
        VirtualMachinesResourceTypes[
          secondaryCloudProviders[2 % secondaryCloudProviders.length]
        ],
      backupStatus: BackupStatus.Protected,
      classifications: {
        environmentDetails: {
          environment: Environment.ProdInternal,
        },
        appsDetails: {
          apps: [
            {
              name: 'Jira',
            },
            {
              name: 'Grafana',
            },
          ],
        },
      },
      tags: { environment: 'internal', service: 'monitoring' },
      subnets: ['10.0.4.0/24'],
      latestSnapshotTime: new Date(new Date(now).setHours(now.getHours() - 8)),
      discoveredTime: new Date(new Date(now).setHours(now.getHours() - 8)),
      sourceStorage: {
        filesCount: 45000,
        sizeBytes: convertToBytes(200, 'GiB'),
        volumeCount: 2,
      },
      snapshotStorage: {
        sizeBytes: convertToBytes(250, 'GB'),
        eonSnapshotCount: 24,
        nonEonSnapshotCount: 4,
      },
      id: '',
      region: '',
      backupPolicies: [],
      securityAssignmentStatus: SecurityAssignmentStatus.SECURITY_ASSIGNED,
      vaults: [],
      providerAccountId: '',
      cloudProvider: Provider.ProviderUnspecified,
      providerResourceId: '',
    },
    {
      resourceName: 'prod-cache-cluster-1',
      resourceType:
        VirtualMachinesResourceTypes[
          secondaryCloudProviders[2 % secondaryCloudProviders.length]
        ],
      backupStatus: BackupStatus.Protected,
      classifications: {
        environmentDetails: {
          environment: Environment.Prod,
        },
        appsDetails: {
          apps: [
            {
              name: 'Redis',
            },
          ],
        },
      },
      tags: { environment: 'production', service: 'cache' },
      subnets: ['10.0.5.0/24'],
      latestSnapshotTime: new Date(new Date(now).setHours(now.getHours() - 3)),
      discoveredTime: new Date(new Date(now).setHours(now.getHours() - 3)),
      sourceStorage: {
        filesCount: 15000,
        sizeBytes: convertToBytes(64, 'GiB'),
        volumeCount: 1,
      },
      snapshotStorage: {
        sizeBytes: convertToBytes(80, 'GB'),
        eonSnapshotCount: 12,
        nonEonSnapshotCount: 11,
      },
      id: '',
      region: '',
      backupPolicies: [],
      securityAssignmentStatus: SecurityAssignmentStatus.SECURITY_ASSIGNED,
      vaults: [],
      providerAccountId: '',
      cloudProvider: Provider.ProviderUnspecified,
      providerResourceId: '',
    },
    {
      resourceName: 'prod-elasticsearch-1',
      resourceType:
        VirtualMachinesResourceTypes[
          secondaryCloudProviders[2 % secondaryCloudProviders.length]
        ],
      backupStatus: BackupStatus.Protected,
      classifications: {
        environmentDetails: {
          environment: Environment.Prod,
        },
        appsDetails: {
          apps: [
            {
              name: 'Elasticsearch',
            },
          ],
        },
      },
      tags: { environment: 'production', service: 'search' },
      subnets: ['10.0.6.0/24'],
      latestSnapshotTime: new Date(new Date(now).setHours(now.getHours() - 5)),
      discoveredTime: new Date(new Date(now).setHours(now.getHours() - 5)),
      sourceStorage: {
        filesCount: 180000,
        sizeBytes: convertToBytes(400, 'GiB'),
        volumeCount: 3,
      },
      snapshotStorage: {
        sizeBytes: convertToBytes(450, 'GB'),
        eonSnapshotCount: 36,
        nonEonSnapshotCount: 9,
      },
      id: '',
      region: '',
      backupPolicies: [],
      securityAssignmentStatus: SecurityAssignmentStatus.SECURITY_ASSIGNED,
      vaults: [],
      providerAccountId: '',
      cloudProvider: Provider.ProviderUnspecified,
      providerResourceId: '',
    },
    {
      resourceName: 'prod-inventory-1',
      resourceType:
        ManagedSQLResourceTypes[
          secondaryCloudProviders[2 % secondaryCloudProviders.length]
        ],
      backupStatus: BackupStatus.Protected,
      classifications: {
        environmentDetails: {
          environment: Environment.Prod,
        },
        appsDetails: {
          apps: [
            {
              name: 'PostgreSQL',
            },
          ],
        },
      },
      resourceProperties: {
        gcpCloudSql: {
          engine: 'postgres',
          engineVersion: '13.3',
          instanceTier: '',
          sizeBytes: convertToBytes(760, 'GiB'),
        },
      },
      tags: { environment: 'production', service: 'search' },
      subnets: ['10.0.6.0/24'],
      latestSnapshotTime: new Date(new Date(now).setHours(now.getHours() - 5)),
      discoveredTime: new Date(new Date(now).setHours(now.getHours() - 5)),
      sourceStorage: {
        filesCount: 180000,
        sizeBytes: convertToBytes(760, 'GiB'),
        volumeCount: 3,
      },
      snapshotStorage: {
        sizeBytes: convertToBytes(450, 'GB'),
        eonSnapshotCount: 36,
        nonEonSnapshotCount: 17,
      },
      id: '',
      region: '',
      backupPolicies: [],
      securityAssignmentStatus: SecurityAssignmentStatus.SECURITY_ASSIGNED,
      vaults: [],
      providerAccountId: '',
      cloudProvider: Provider.ProviderUnspecified,
      providerResourceId: '',
    },
    {
      resourceName: 'prod-customers-1',
      resourceType:
        ManagedSQLResourceTypes[
          secondaryCloudProviders[2 % secondaryCloudProviders.length]
        ],
      backupStatus: BackupStatus.Protected,
      classifications: {
        environmentDetails: {
          environment: Environment.Prod,
        },
        appsDetails: {
          apps: [
            {
              name: 'MySQL',
            },
          ],
        },
      },
      resourceProperties: {
        gcpCloudSql: {
          engine: 'mysql',
          engineVersion: '8',
          instanceTier: '',
          sizeBytes: convertToBytes(20, 'GiB'),
        },
      },
      tags: { environment: 'production', service: 'search' },
      subnets: ['10.0.6.0/24'],
      latestSnapshotTime: new Date(new Date(now).setHours(now.getHours() - 5)),
      discoveredTime: new Date(new Date(now).setHours(now.getHours() - 5)),
      sourceStorage: {
        filesCount: 180000,
        sizeBytes: convertToBytes(20, 'GiB'),
        volumeCount: 3,
      },
      snapshotStorage: {
        sizeBytes: convertToBytes(450, 'GB'),
        eonSnapshotCount: 36,
        nonEonSnapshotCount: 25,
      },
      id: '',
      region: '',
      backupPolicies: [],
      securityAssignmentStatus: SecurityAssignmentStatus.SECURITY_ASSIGNED,
      vaults: [],
      providerAccountId: '',
      cloudProvider: Provider.ProviderUnspecified,
      providerResourceId: '',
    },
    {
      resourceName: 'prod-media-assets',
      resourceType:
        StorageResourceTypes[
          secondaryCloudProviders[2 % secondaryCloudProviders.length]
        ],
      backupStatus: BackupStatus.Protected,
      classifications: {
        environmentDetails: {
          environment: Environment.Prod,
        },
        dataClassesDetails: {
          dataClasses: [DataClass.Pii],
        },
      },
      tags: { environment: 'production', service: 'media-content' },
      latestSnapshotTime: new Date(new Date(now).setHours(now.getHours() - 3)),
      discoveredTime: new Date(new Date(now).setHours(now.getHours() - 3)),
      sourceStorage: {
        filesCount: 320000,
        sizeBytes: convertToBytes(2500, 'GiB'),
      },
      snapshotStorage: {
        sizeBytes: convertToBytes(2800, 'GB'),
        eonSnapshotCount: 24,
        nonEonSnapshotCount: 0,
      },
      id: '',
      region: '',
      backupPolicies: [],
      securityAssignmentStatus: SecurityAssignmentStatus.SECURITY_ASSIGNED,
      vaults: [],
      providerAccountId: '',
      cloudProvider: Provider.ProviderUnspecified,
      providerResourceId: '',
    },
    {
      resourceName: 'finance-reports-archive',
      resourceType:
        StorageResourceTypes[
          secondaryCloudProviders[2 % secondaryCloudProviders.length]
        ],
      backupStatus: BackupStatus.Protected,
      classifications: {
        environmentDetails: {
          environment: Environment.Prod,
        },
        dataClassesDetails: {
          dataClasses: [DataClass.Fi],
        },
      },
      tags: { environment: 'production', service: 'finance', type: 'reports' },
      latestSnapshotTime: new Date(new Date(now).setHours(now.getHours() - 4)),
      discoveredTime: new Date(new Date(now).setHours(now.getHours() - 4)),
      sourceStorage: {
        filesCount: 45000,
        sizeBytes: convertToBytes(800, 'GiB'),
      },
      snapshotStorage: {
        sizeBytes: convertToBytes(900, 'GB'),
        eonSnapshotCount: 36,
        nonEonSnapshotCount: 12,
      },
      id: '',
      region: '',
      backupPolicies: [],
      securityAssignmentStatus: SecurityAssignmentStatus.SECURITY_ASSIGNED,
      vaults: [],
      providerAccountId: '',
      cloudProvider: Provider.ProviderUnspecified,
      providerResourceId: '',
    },
    {
      resourceName: 'dev-shared-assets',
      resourceType:
        StorageResourceTypes[
          secondaryCloudProviders[2 % secondaryCloudProviders.length]
        ],
      backupStatus: BackupStatus.Protected,
      classifications: {
        environmentDetails: {
          environment: Environment.Stage,
        },
      },
      tags: { environment: 'development', service: 'shared-resources' },
      latestSnapshotTime: new Date(new Date(now).setHours(now.getHours() - 5)),
      discoveredTime: new Date(new Date(now).setHours(now.getHours() - 5)),
      sourceStorage: {
        filesCount: 15000,
        sizeBytes: convertToBytes(350, 'GiB'),
      },
      snapshotStorage: {
        sizeBytes: convertToBytes(400, 'GB'),
        eonSnapshotCount: 12,
        nonEonSnapshotCount: 8,
      },
      id: '',
      region: '',
      backupPolicies: [],
      securityAssignmentStatus: SecurityAssignmentStatus.SECURITY_ASSIGNED,
      vaults: [],
      providerAccountId: '',
      cloudProvider: Provider.ProviderUnspecified,
      providerResourceId: '',
    },
    {
      resourceName: 'prod-user-profiles',
      resourceType:
        NoSqlDBResourceTypes[
          secondaryCloudProviders[2 % secondaryCloudProviders.length]
        ],
      backupStatus: BackupStatus.Protected,
      classifications: {
        environmentDetails: {
          environment: Environment.Prod,
        },
        dataClassesDetails: {
          dataClasses: [DataClass.Pii],
        },
        appsDetails: {
          apps: [
            {
              name: 'Firestore',
            },
          ],
        },
      },
      tags: { environment: 'production', service: 'user-management' },
      latestSnapshotTime: new Date(new Date(now).setHours(now.getHours() - 2)),
      discoveredTime: new Date(new Date(now).setHours(now.getHours() - 2)),
      sourceStorage: {
        filesCount: 0,
        sizeBytes: convertToBytes(120, 'GiB'),
      },
      snapshotStorage: {
        sizeBytes: convertToBytes(150, 'GB'),
        eonSnapshotCount: 24,
        nonEonSnapshotCount: 0,
      },
      id: '',
      region: '',
      backupPolicies: [],
      securityAssignmentStatus: SecurityAssignmentStatus.SECURITY_ASSIGNED,
      vaults: [],
      providerAccountId: '',
      cloudProvider: Provider.ProviderUnspecified,
      providerResourceId: '',
    },
    {
      resourceName: 'prod-product-library',
      resourceType:
        NoSqlDBResourceTypes[
          secondaryCloudProviders[2 % secondaryCloudProviders.length]
        ],
      backupStatus: BackupStatus.Protected,
      classifications: {
        environmentDetails: {
          environment: Environment.Prod,
        },
        appsDetails: {
          apps: [
            {
              name: 'Firestore',
            },
          ],
        },
      },
      tags: { environment: 'production', service: 'product-catalog' },
      latestSnapshotTime: new Date(new Date(now).setHours(now.getHours() - 4)),
      discoveredTime: new Date(new Date(now).setHours(now.getHours() - 4)),
      sourceStorage: {
        filesCount: 0,
        sizeBytes: convertToBytes(350, 'GiB'),
      },
      snapshotStorage: {
        sizeBytes: convertToBytes(400, 'GB'),
        eonSnapshotCount: 36,
        nonEonSnapshotCount: 6,
      },
      id: '',
      region: '',
      backupPolicies: [],
      securityAssignmentStatus: SecurityAssignmentStatus.SECURITY_ASSIGNED,
      vaults: [],
      providerAccountId: '',
      cloudProvider: Provider.ProviderUnspecified,
      providerResourceId: '',
    },
    {
      resourceName: 'analytics-events-store',
      resourceType:
        NoSqlDBResourceTypes[
          secondaryCloudProviders[2 % secondaryCloudProviders.length]
        ],
      backupStatus: BackupStatus.Protected,
      classifications: {
        environmentDetails: {
          environment: Environment.Prod,
        },
        appsDetails: {
          apps: [
            {
              name: 'Firestore',
            },
          ],
        },
      },
      tags: { environment: 'production', service: 'analytics' },
      latestSnapshotTime: new Date(new Date(now).setHours(now.getHours() - 3)),
      discoveredTime: new Date(new Date(now).setHours(now.getHours() - 3)),
      sourceStorage: {
        filesCount: 0,
        sizeBytes: convertToBytes(1800, 'GiB'),
      },
      snapshotStorage: {
        sizeBytes: convertToBytes(2000, 'GB'),
        eonSnapshotCount: 12,
        nonEonSnapshotCount: 4,
      },
      id: '',
      region: '',
      backupPolicies: [],
      securityAssignmentStatus: SecurityAssignmentStatus.SECURITY_ASSIGNED,
      vaults: [],
      providerAccountId: '',
      cloudProvider: Provider.ProviderUnspecified,
      providerResourceId: '',
    },
    {
      resourceName: 'analytics_data_warehouse',
      resourceType: ResourceType.GcpBigQuery,
      backupStatus: BackupStatus.Protected,
      classifications: {
        environmentDetails: {
          environment: Environment.Prod,
        },
        dataClassesDetails: {
          dataClasses: [DataClass.Pii],
        },
        appsDetails: {
          apps: [
            {
              name: 'BigQuery',
            },
          ],
        },
      },
      tags: { environment: 'production', service: 'analytics' },
      latestSnapshotTime: new Date(new Date(now).setHours(now.getHours() - 4)),
      discoveredTime: new Date(new Date(now).setHours(now.getHours() - 4)),
      sourceStorage: {
        filesCount: 0,
        sizeBytes: convertToBytes(5000, 'GiB'),
      },
      snapshotStorage: {
        sizeBytes: convertToBytes(5500, 'GB'),
        eonSnapshotCount: 12,
        nonEonSnapshotCount: 0,
      },
      id: '',
      region: '',
      backupPolicies: [],
      securityAssignmentStatus: SecurityAssignmentStatus.SECURITY_ASSIGNED,
      vaults: [],
      providerAccountId: '',
      cloudProvider: Provider.ProviderUnspecified,
      providerResourceId: '',
    },
    {
      resourceName: 'finance_reporting_db',
      resourceType: ResourceType.GcpBigQuery,
      backupStatus: BackupStatus.Protected,
      classifications: {
        environmentDetails: {
          environment: Environment.Prod,
        },
        dataClassesDetails: {
          dataClasses: [DataClass.Fi],
        },
        appsDetails: {
          apps: [
            {
              name: 'BigQuery',
            },
          ],
        },
      },
      tags: { environment: 'production', service: 'finance' },
      latestSnapshotTime: new Date(new Date(now).setHours(now.getHours() - 5)),
      discoveredTime: new Date(new Date(now).setHours(now.getHours() - 5)),
      sourceStorage: {
        filesCount: 0,
        sizeBytes: convertToBytes(2200, 'GiB'),
      },
      snapshotStorage: {
        sizeBytes: convertToBytes(2400, 'GB'),
        eonSnapshotCount: 24,
        nonEonSnapshotCount: 8,
      },
      id: '',
      region: '',
      backupPolicies: [],
      securityAssignmentStatus: SecurityAssignmentStatus.SECURITY_ASSIGNED,
      vaults: [],
      providerAccountId: '',
      cloudProvider: Provider.ProviderUnspecified,
      providerResourceId: '',
    },
    {
      resourceName: 'machine_learning_datasets',
      resourceType: ResourceType.GcpBigQuery,
      backupStatus: BackupStatus.Protected,
      classifications: {
        environmentDetails: {
          environment: Environment.Prod,
        },
        appsDetails: {
          apps: [
            {
              name: 'BigQuery',
            },
          ],
        },
      },
      tags: { environment: 'production', service: 'machine-learning' },
      latestSnapshotTime: new Date(new Date(now).setHours(now.getHours() - 3)),
      discoveredTime: new Date(new Date(now).setHours(now.getHours() - 3)),
      sourceStorage: {
        filesCount: 0,
        sizeBytes: convertToBytes(3500, 'GiB'),
      },
      snapshotStorage: {
        sizeBytes: convertToBytes(3800, 'GB'),
        eonSnapshotCount: 36,
        nonEonSnapshotCount: 12,
      },
      id: '',
      region: '',
      backupPolicies: [],
      securityAssignmentStatus: SecurityAssignmentStatus.SECURITY_ASSIGNED,
      vaults: [],
      providerAccountId: '',
      cloudProvider: Provider.ProviderUnspecified,
      providerResourceId: '',
    },
    {
      resourceName: 'prod-frontend-cluster',
      resourceType:
        KubernetesResourceTypes[
          secondaryCloudProviders[1 % secondaryCloudProviders.length]
        ],
      backupStatus: BackupStatus.Protected,
      classifications: {
        environmentDetails: {
          environment: Environment.Prod,
        },
      },
      resourceProperties: {
        awsEksNamespace: {
          secretsCount: 25,
          clusterName: 'production-workflow-cluster',
          name: 'prod-namespace',
          status: 'Active',
          creationTime: new Date('2025-02-20T14:20:48Z'),
          persistentVolumeClaims: [
            {
              providerResourceId: '',
              path: 'production-cluster-dynamic-pvc-d4b71706-8aae-4083-918d-af2d0242251a',
              availabilityZone: 'us-east-1b',
              volumeSettings: {
                type: 'gp3',
                iops: 3000,
                throughput: 125,
                sizeBytes: 2147483648,
              },
              fsType: 'ext4',
              pvName: 'pvc-d4b71706-8aae-4083-918d-af2d0242251a',
              pvcName: 'prod-pvc',
              accessModes: [KubernetesAccessMode.ReadWriteOnce],
            },
            {
              providerResourceId: '',
              path: 'production-cluster-dynamic-pvc-2a979978-f4ac-41e8-bb2b-eed354291900',
              availabilityZone: 'us-east-1b',
              volumeSettings: {
                type: 'gp3',
                iops: 3000,
                throughput: 125,
                sizeBytes: 1073741824,
              },
              fsType: 'ext4',
              pvName: 'pvc-2a979978-f4ac-41e8-bb2b-eed354291900',
              pvcName: 'prod-pvc-1',
              accessModes: [KubernetesAccessMode.ReadWriteOnce],
            },
            {
              providerResourceId: '',
              path: 'production-cluster-dynamic-pvc-cf2d9b48-23fc-47a8-a27f-4e3ff9348466',
              availabilityZone: 'us-east-1b',
              volumeSettings: {
                type: 'gp3',
                iops: 3000,
                throughput: 125,
                sizeBytes: 2147483648,
              },
              fsType: 'ext4',
              pvName: 'pvc-cf2d9b48-23fc-47a8-a27f-4e3ff9348466',
              pvcName: 'prod-pvc-2',
              accessModes: [KubernetesAccessMode.ReadWriteOnce],
            },
            {
              providerResourceId: '',
              path: 'production-cluster-dynamic-pvc-7b37f18d-5955-4987-b98b-5074647af366',
              availabilityZone: 'us-east-1b',
              volumeSettings: {
                type: 'gp3',
                iops: 3000,
                throughput: 125,
                sizeBytes: 3221225472,
              },
              fsType: 'ext4',
              pvName: 'pvc-7b37f18d-5955-4987-b98b-5074647af366',
              pvcName: 'prod-pvc-3',
              accessModes: [KubernetesAccessMode.ReadWriteOnce],
            },
          ],
          labels: {
            'kubernetes.io/metadata.name': 'prod-namespace',
          },
          resourceVersion: '9189',
        },
        gcpGkeNamespace: {
          secretsCount: 25,
          clusterName: 'production-workflow-cluster',
          name: 'prod-namespace',
          status: 'Active',
          creationTime: new Date('2025-02-20T14:20:48Z'),
          persistentVolumeClaims: [
            {
              providerResourceId: '',
              path: 'production-cluster-dynamic-pvc-d4b71706-8aae-4083-918d-af2d0242251a',
              availabilityZone: '',
              volumeSettings: {
                type: '',
                sizeBytes: 2147483648,
              },
              fsType: '',
              pvName: 'pvc-d4b71706-8aae-4083-918d-af2d0242251a',
              pvcName: 'prod-pvc',
              accessModes: [KubernetesAccessMode.ReadWriteOnce],
            },
            {
              providerResourceId: '',
              path: 'production-cluster-dynamic-pvc-2a979978-f4ac-41e8-bb2b-eed354291900',
              availabilityZone: '',
              volumeSettings: {
                type: '',
                sizeBytes: 1073741824,
              },
              fsType: '',
              pvName: 'pvc-2a979978-f4ac-41e8-bb2b-eed354291900',
              pvcName: 'prod-pvc-1',
              accessModes: [KubernetesAccessMode.ReadWriteOnce],
            },
            {
              providerResourceId: '',
              path: 'production-cluster-dynamic-pvc-cf2d9b48-23fc-47a8-a27f-4e3ff9348466',
              availabilityZone: '',
              volumeSettings: {
                type: '',
                sizeBytes: 2147483648,
              },
              fsType: '',
              pvName: 'pvc-cf2d9b48-23fc-47a8-a27f-4e3ff9348466',
              pvcName: 'prod-pvc-2',
              accessModes: [KubernetesAccessMode.ReadWriteOnce],
            },
            {
              providerResourceId: '',
              path: 'production-cluster-dynamic-pvc-7b37f18d-5955-4987-b98b-5074647af366',
              availabilityZone: '',
              volumeSettings: {
                type: '',
                sizeBytes: 3221225472,
              },
              fsType: '',
              pvName: 'pvc-7b37f18d-5955-4987-b98b-5074647af366',
              pvcName: 'prod-pvc-3',
              accessModes: [KubernetesAccessMode.ReadWriteOnce],
            },
          ],
          labels: {
            'kubernetes.io/metadata.name': 'prod-namespace',
          },
          resourceVersion: '9189',
        },
        azureAksNamespace: {
          secretsCount: 25,
          clusterName: 'production-workflow-cluster',
          name: 'prod-namespace',
          status: 'Active',
          creationTime: new Date('2025-02-20T14:20:48Z'),
          persistentVolumeClaims: [
            {
              providerResourceId: '',
              path: 'production-cluster-dynamic-pvc-d4b71706-8aae-4083-918d-af2d0242251a',
              availabilityZone: '',
              volumeSettings: {
                type: '',
                sizeBytes: 2147483648,
              },
              fsType: '',
              pvName: 'pvc-d4b71706-8aae-4083-918d-af2d0242251a',
              pvcName: 'prod-pvc',
              accessModes: [KubernetesAccessMode.ReadWriteOnce],
            },
            {
              providerResourceId: '',
              path: 'production-cluster-dynamic-pvc-2a979978-f4ac-41e8-bb2b-eed354291900',
              availabilityZone: '',
              volumeSettings: {
                type: '',
                sizeBytes: 1073741824,
              },
              fsType: '',
              pvName: 'pvc-2a979978-f4ac-41e8-bb2b-eed354291900',
              pvcName: 'prod-pvc-1',
              accessModes: [KubernetesAccessMode.ReadWriteOnce],
            },
            {
              providerResourceId: '',
              path: 'production-cluster-dynamic-pvc-cf2d9b48-23fc-47a8-a27f-4e3ff9348466',
              availabilityZone: '',
              volumeSettings: {
                type: '',
                sizeBytes: 2147483648,
              },
              fsType: '',
              pvName: 'pvc-cf2d9b48-23fc-47a8-a27f-4e3ff9348466',
              pvcName: 'prod-pvc-2',
              accessModes: [KubernetesAccessMode.ReadWriteOnce],
            },
            {
              providerResourceId: '',
              path: 'production-cluster-dynamic-pvc-7b37f18d-5955-4987-b98b-5074647af366',
              availabilityZone: '',
              volumeSettings: {
                type: '',
                sizeBytes: 3221225472,
              },
              fsType: '',
              pvName: 'pvc-7b37f18d-5955-4987-b98b-5074647af366',
              pvcName: 'prod-pvc-3',
              accessModes: [KubernetesAccessMode.ReadWriteOnce],
            },
          ],
          labels: {
            'kubernetes.io/metadata.name': 'prod-namespace',
          },
          resourceVersion: '9189',
        },
      },
      tags: { environment: 'production', service: 'frontend' },
      subnets: ['10.2.0.0/16'],
      latestSnapshotTime: new Date(new Date(now).setHours(now.getHours() - 3)),
      discoveredTime: new Date(new Date(now).setHours(now.getHours() - 3)),
      sourceStorage: {
        filesCount: 0,
        sizeBytes: convertToBytes(180, 'GiB'),
        volumeCount: 8,
      },
      snapshotStorage: {
        sizeBytes: convertToBytes(220, 'GB'),
        eonSnapshotCount: 36,
        nonEonSnapshotCount: 0,
      },
      id: '',
      region: '',
      backupPolicies: [],
      securityAssignmentStatus: SecurityAssignmentStatus.SECURITY_ASSIGNED,
      vaults: [],
      providerAccountId: '',
      cloudProvider: Provider.ProviderUnspecified,
      providerResourceId: '',
    },
    {
      resourceName: 'prod-batch-processing',
      resourceType:
        KubernetesResourceTypes[
          secondaryCloudProviders[2 % secondaryCloudProviders.length]
        ],
      backupStatus: BackupStatus.Protected,
      classifications: {
        environmentDetails: {
          environment: Environment.Prod,
        },
        dataClassesDetails: {
          dataClasses: [DataClass.Fi],
        },
      },
      resourceProperties: {
        awsEksNamespace: {
          secretsCount: 25,
          clusterName: 'production-analytics-cluster',
          name: 'prod-namespace',
          status: 'Active',
          creationTime: new Date('2025-02-20T14:20:48Z'),
          persistentVolumeClaims: [
            {
              providerResourceId: '',
              path: 'production-cluster-dynamic-pvc-d4b71706-8aae-4083-918d-af2d0242251a',
              availabilityZone: 'us-east-1b',
              volumeSettings: {
                type: 'gp3',
                iops: 3000,
                throughput: 125,
                sizeBytes: 2147483648,
              },
              fsType: 'ext4',
              pvName: 'pvc-d4b71706-8aae-4083-918d-af2d0242251a',
              pvcName: 'prod-pvc',
              accessModes: [KubernetesAccessMode.ReadWriteOnce],
            },
            {
              providerResourceId: '',
              path: 'production-cluster-dynamic-pvc-2a979978-f4ac-41e8-bb2b-eed354291900',
              availabilityZone: 'us-east-1b',
              volumeSettings: {
                type: 'gp3',
                iops: 3000,
                throughput: 125,
                sizeBytes: 1073741824,
              },
              fsType: 'ext4',
              pvName: 'pvc-2a979978-f4ac-41e8-bb2b-eed354291900',
              pvcName: 'prod-pvc-1',
              accessModes: [KubernetesAccessMode.ReadWriteOnce],
            },
            {
              providerResourceId: '',
              path: 'production-cluster-dynamic-pvc-cf2d9b48-23fc-47a8-a27f-4e3ff9348466',
              availabilityZone: 'us-east-1b',
              volumeSettings: {
                type: 'gp3',
                iops: 3000,
                throughput: 125,
                sizeBytes: 2147483648,
              },
              fsType: 'ext4',
              pvName: 'pvc-cf2d9b48-23fc-47a8-a27f-4e3ff9348466',
              pvcName: 'prod-pvc-2',
              accessModes: [KubernetesAccessMode.ReadWriteOnce],
            },
            {
              providerResourceId: '',
              path: 'production-cluster-dynamic-pvc-7b37f18d-5955-4987-b98b-5074647af366',
              availabilityZone: 'us-east-1b',
              volumeSettings: {
                type: 'gp3',
                iops: 3000,
                throughput: 125,
                sizeBytes: 3221225472,
              },
              fsType: 'ext4',
              pvName: 'pvc-7b37f18d-5955-4987-b98b-5074647af366',
              pvcName: 'prod-pvc-3',
              accessModes: [KubernetesAccessMode.ReadWriteOnce],
            },
          ],
          labels: {
            'kubernetes.io/metadata.name': 'prod-namespace',
          },
          resourceVersion: '9189',
        },
        gcpGkeNamespace: {
          secretsCount: 25,
          clusterName: 'production-analytics-cluster',
          name: 'prod-namespace',
          status: 'Active',
          creationTime: new Date('2025-02-20T14:20:48Z'),
          persistentVolumeClaims: [
            {
              providerResourceId: '',
              path: 'production-cluster-dynamic-pvc-d4b71706-8aae-4083-918d-af2d0242251a',
              availabilityZone: '',
              volumeSettings: {
                type: '',
                sizeBytes: 2147483648,
              },
              fsType: '',
              pvName: 'pvc-d4b71706-8aae-4083-918d-af2d0242251a',
              pvcName: 'prod-pvc',
              accessModes: [KubernetesAccessMode.ReadWriteOnce],
            },
            {
              providerResourceId: '',
              path: 'production-cluster-dynamic-pvc-2a979978-f4ac-41e8-bb2b-eed354291900',
              availabilityZone: '',
              volumeSettings: {
                type: '',
                sizeBytes: 1073741824,
              },
              fsType: '',
              pvName: 'pvc-2a979978-f4ac-41e8-bb2b-eed354291900',
              pvcName: 'prod-pvc-1',
              accessModes: [KubernetesAccessMode.ReadWriteOnce],
            },
            {
              providerResourceId: '',
              path: 'production-cluster-dynamic-pvc-cf2d9b48-23fc-47a8-a27f-4e3ff9348466',
              availabilityZone: '',
              volumeSettings: {
                type: '',
                sizeBytes: 2147483648,
              },
              fsType: '',
              pvName: 'pvc-cf2d9b48-23fc-47a8-a27f-4e3ff9348466',
              pvcName: 'prod-pvc-2',
              accessModes: [KubernetesAccessMode.ReadWriteOnce],
            },
            {
              providerResourceId: '',
              path: 'production-cluster-dynamic-pvc-7b37f18d-5955-4987-b98b-5074647af366',
              availabilityZone: '',
              volumeSettings: {
                type: '',
                sizeBytes: 3221225472,
              },
              fsType: '',
              pvName: 'pvc-7b37f18d-5955-4987-b98b-5074647af366',
              pvcName: 'prod-pvc-3',
              accessModes: [KubernetesAccessMode.ReadWriteOnce],
            },
          ],
          labels: {
            'kubernetes.io/metadata.name': 'prod-namespace',
          },
          resourceVersion: '9189',
        },
        azureAksNamespace: {
          secretsCount: 25,
          clusterName: 'production-analytics-cluster',
          name: 'prod-namespace',
          status: 'Active',
          creationTime: new Date('2025-02-20T14:20:48Z'),
          persistentVolumeClaims: [
            {
              providerResourceId: '',
              path: 'production-cluster-dynamic-pvc-d4b71706-8aae-4083-918d-af2d0242251a',
              availabilityZone: '',
              volumeSettings: {
                type: '',
                sizeBytes: 2147483648,
              },
              fsType: '',
              pvName: 'pvc-d4b71706-8aae-4083-918d-af2d0242251a',
              pvcName: 'prod-pvc',
              accessModes: [KubernetesAccessMode.ReadWriteOnce],
            },
            {
              providerResourceId: '',
              path: 'production-cluster-dynamic-pvc-2a979978-f4ac-41e8-bb2b-eed354291900',
              availabilityZone: '',
              volumeSettings: {
                type: '',
                sizeBytes: 1073741824,
              },
              fsType: '',
              pvName: 'pvc-2a979978-f4ac-41e8-bb2b-eed354291900',
              pvcName: 'prod-pvc-1',
              accessModes: [KubernetesAccessMode.ReadWriteOnce],
            },
            {
              providerResourceId: '',
              path: 'production-cluster-dynamic-pvc-cf2d9b48-23fc-47a8-a27f-4e3ff9348466',
              availabilityZone: '',
              volumeSettings: {
                type: '',
                sizeBytes: 2147483648,
              },
              fsType: '',
              pvName: 'pvc-cf2d9b48-23fc-47a8-a27f-4e3ff9348466',
              pvcName: 'prod-pvc-2',
              accessModes: [KubernetesAccessMode.ReadWriteOnce],
            },
            {
              providerResourceId: '',
              path: 'production-cluster-dynamic-pvc-7b37f18d-5955-4987-b98b-5074647af366',
              availabilityZone: '',
              volumeSettings: {
                type: '',
                sizeBytes: 3221225472,
              },
              fsType: '',
              pvName: 'pvc-7b37f18d-5955-4987-b98b-5074647af366',
              pvcName: 'prod-pvc-3',
              accessModes: [KubernetesAccessMode.ReadWriteOnce],
            },
          ],
          labels: {
            'kubernetes.io/metadata.name': 'prod-namespace',
          },
          resourceVersion: '9189',
        },
      },
      tags: { environment: 'production', service: 'batch-processing' },
      subnets: ['10.3.0.0/16'],
      latestSnapshotTime: new Date(new Date(now).setHours(now.getHours() - 4)),
      discoveredTime: new Date(new Date(now).setHours(now.getHours() - 4)),
      sourceStorage: {
        filesCount: 0,
        sizeBytes: convertToBytes(750, 'GiB'),
        volumeCount: 20,
      },
      snapshotStorage: {
        sizeBytes: convertToBytes(850, 'GB'),
        eonSnapshotCount: 48,
        nonEonSnapshotCount: 12,
      },
      id: '',
      region: '',
      backupPolicies: [],
      securityAssignmentStatus: SecurityAssignmentStatus.SECURITY_ASSIGNED,
      vaults: [],
      providerAccountId: '',
      cloudProvider: Provider.ProviderUnspecified,
      providerResourceId: '',
    },
    {
      backupStatus: BackupStatus.Protected,
      resourceName: 'sqlvm1',
      resourceType: ResourceType.AzureSqlVirtualMachine,
      classifications: {
        environmentDetails: {
          environment: Environment.Prod,
        },
        appsDetails: {
          apps: [
            {
              name: 'MSSQLServer',
            },
          ],
        },
      },
      latestSnapshotTime: new Date(new Date(now).setHours(now.getHours() - 4)),
      discoveredTime: new Date(new Date(now).setHours(now.getHours() - 4)),
      tags: {},
      snapshotStorage: {},
      sourceStorage: {
        sizeBytes: 274877906944,
      },
      id: '',
      region: '',
      backupPolicies: [],
      securityAssignmentStatus: SecurityAssignmentStatus.SECURITY_ASSIGNED,
      vaults: [],
      providerAccountId: '',
      cloudProvider: Provider.ProviderUnspecified,
      providerResourceId: '',
    },
  ];

  // Set derived properties
  inventory.forEach((item, index) => {
    item.id = `0004c8b2-28bc-45a8-b4a3-487bc50ed00${index < 10 ? '0' : ''}${index}`;
    item.cloudProvider = ResourceTypes[item.resourceType].provider;
    item.securityScan = demoSettings.ransomware ? item.securityScan : undefined;

    item.providerAccountId =
      sourceAccounts.find((x) => x.cloudProvider === item.cloudProvider)
        ?.providerAccountId || '';

    item.vpc = vpcs[item.cloudProvider];
    item.region = Object.keys(Regions[item.cloudProvider])[0];
    item.providerResourceId = generateProviderResourceId(item);
    item.backupPolicies = getBackupPolicies(item, demoSettings);
    item.resourceProperties?.awsEksNamespace?.persistentVolumeClaims?.forEach(
      (vol) => {
        vol.providerResourceId = `vol-${generateRandomAlphanumeric(8)}`;
      }
    );
    item.resourceProperties?.gcpGkeNamespace?.persistentVolumeClaims?.forEach(
      (vol) => {
        vol.providerResourceId = `projects/${item.providerAccountId}/zones/${item.region}/disks/${vol.pvName}`;
      }
    );

    item.resourceProperties?.azureAksNamespace?.persistentVolumeClaims?.forEach(
      (vol) => {
        vol.providerResourceId = `/subscriptions/${item.providerAccountId}/resourceGroups/rg-disks/providers/Microsoft.Compute/disks/${vol.pvName}`;
      }
    );

    item.vaults = item.backupPolicies.reduce<BackupVault[]>((agg, policy) => {
      policy.attachedResources++;
      policy.schedules
        .map((schedule) => vaults.find((x) => x.id === schedule.vaultId))
        .forEach((vault) => {
          if (vault && !agg.includes(vault)) {
            agg.push(vault);
          }
        });

      return agg;
    }, []);
  });

  return inventory.filter(
    (item) =>
      item.cloudProvider !== Provider.ProviderUnspecified &&
      demoSettings[item.cloudProvider]
  );
};
